
import React, { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { flushSync } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ProjectState, HistoryItem, Fixture, type FixtureSelectOptions } from './types';
import Sidebar from './components/Sidebar';
import Editor from './components/Editor';
import HistoryPanel from './components/HistoryPanel';
import UpscalePanel from './components/UpscalePanel';
import LightReconstructPanel from './components/LightReconstructPanel';
import TextReconstructPanel from './components/TextReconstructPanel';
import PhysicalReconstructPanel from './components/PhysicalReconstructPanel';
import ToneMappingModal from './components/ToneMappingModal';
import RunningHubJobRecoveryBar from './components/RunningHubJobRecoveryBar';
import {
  dispatchRunningHubHistoryReload,
  dispatchRunningHubHistoryReset,
} from './utils/runningHubHistoryScope';
import DepthModal from './components/DepthModal';
import RecognitionSyncReportModal, { type RecognitionSyncReport } from './components/RecognitionSyncReportModal';
import AuthModal, { type AuthLang } from './components/AuthModal';
import PasswordResetScreen from './components/PasswordResetScreen';
import PaymentCenter from './components/PaymentCenter';
import type { RechargePlanId } from './components/rechargePlans';
import UserAccount from './components/UserAccount';
import { ToastProvider } from './components/Toast';
import { renderEnhancedLighting, analyzeImageForFixtures } from './services/geminiService';
import { supabase } from './src/lib/supabaseClient';
import { api } from './services/apiClient';
import type { RunningHubRecoveryRequest } from './utils/applyRunningHubJobRecovery';
import Maintenance from './components/Maintenance';
import { initDevToolsProtection } from './utils/devToolsProtection';
import { useAccount } from './contexts/AccountContext';
import {
  peekPasswordRecoveryHint,
  clearPasswordRecoveryHint,
} from './utils/passwordRecoverySessionHint';
import { consumeRecoveryQueryIfPresent } from './utils/recoveryFromEmailQuery';
import { resolveAuthEmail } from './utils/resolveAuthEmail';

const AdminLazyPage = lazy(() => import('./pages/Admin'));
import { fetchUserLightingPresets, type LightingPresetRow } from './services/lightingPresets';
import { useCreditDeduction } from './hooks/useCreditDeduction';
import {
  normalizeStripContext,
  normalizeStripAxis,
  defaultAxisForStripContext,
} from './utils/stripLightContext';
import {
  normalizeDownlightContext,
  normalizeBeamProfile,
  defaultBeamProfileForContext,
} from './utils/downlightContext';
import {
  createOffscreenSpatialMaps,
  processSpatialMapping,
  type SpatialCoordinateMeta,
  type SpatialFixtureHint,
} from './utils/spatialLightingMapping';
import { buildPhotopeaOpenUrl, isTencentCosPublicUrl } from './utils/photopea';
import { isGifFile } from './utils/imageFormatPolicy';
import { t as translateAppI18n } from './utils/i18n';
import {
  normalizeFixtureForRender,
  normalizeFixtureType,
  normalizeFixtureLabel,
  ensureSpatialMapDataUrlOrNull,
} from './utils/fixtureNormalize';

/** 官网订阅区「立即充值」等：带 query 进入 app 时暂存，登录后再打开充值中心（未登录不能只显示登录页而丢失意图） */
const PENDING_PAYMENT_FROM_LANDING_KEY = 'nemo_pending_payment_from_landing';
/** 未登录访问 #admin 时写入；登录成功后用于恢复 hash（避免 replaceState / 竞态丢失 #admin） */
const NEMO_POST_LOGIN_ADMIN_KEY = 'nemolight_post_login_admin_v1';

type RenderResultLike =
  | string
  | { image: string; cosUrl?: string; balance?: number; imageCredits?: number }
  | null
  | undefined;

/** 画布/预览：优先用接口里的 base64，避免个别环境下 COS 直链未就绪 */
function displayRenderUrl(r: RenderResultLike): string {
  if (!r) return '';
  if (typeof r === 'string') return r;
  return r.image || '';
}

/**
 * 历史列表主键：与数据库 rendered_image_url 一致时用 COS，避免「本地 base64 + 库 https」被当成两条。
 */
function historyRenderUrl(r: RenderResultLike): string {
  if (!r) return '';
  if (typeof r === 'string') return r;
  if (r.cosUrl && /^https?:\/\//i.test(r.cosUrl)) return r.cosUrl;
  return r.image || '';
}

// 获取API基础URL（用于维护模式检测）
const getApiBaseUrl = () => {
  // 强制使用相对路径（通过 Nginx 代理到后端）
  // 不再使用 localhost，因为生产环境应该通过 Nginx 代理
  return '';
};

const App: React.FC = () => {
  const { refreshAccount, invalidateAccount } = useAccount();
  /** 同一支付成功 URL 只处理一次，避免打开账户中心等导致 effect 重入 */
  const handledPaymentSuccessRef = useRef<string | null>(null);

  // 积分扣费 Hook（用于灯光渲染）
  const { checkBalance: checkRenderCredits } = useCreditDeduction({
    language: 'zh',
    onInsufficientBalance: (required, available) => {
      alert(`积分不足：灯光渲染需要 ${required} 积分，当前 ${available} 积分，请及时充值`);
    },
    onDeductionError: (error) => {
      alert(`扣费失败：${error}`);
    }
  });
  
  const [project, setProject] = useState<ProjectState>({
    originalImage: null,
    renderedImage: null,
    isProcessing: false,
    isScanning: false,
    selectedFixtureIds: [],
    fixtures: [],
    depthImageDataUrl: null,
    normalImageDataUrl: null,
    spatialCoordinateMeta: null,
  });
  
  // 🔥 保存压缩图的尺寸，用于坐标归一化
  const [compressedImageDimensions, setCompressedImageDimensions] = useState<{ width: number; height: number } | null>(null);
  // 🔥 保存原图尺寸，用于渲染时确保输出尺寸和输入一致
  const [originalImageDimensions, setOriginalImageDimensions] = useState<{ width: number; height: number } | null>(null);
  
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  
  // 🔥 监听 isHistoryOpen 变化，输出调试信息
  useEffect(() => {
    console.log('[App.tsx] 🔍 isHistoryOpen 状态变化:', isHistoryOpen);
    console.log('[App.tsx] 🔍 当前历史记录数量:', history.length);
    if (isHistoryOpen) {
      console.log('[App.tsx] ✅✅✅ 历史面板应该已打开 ✅✅✅');
    } else {
      console.log('[App.tsx] ❌ 历史面板已关闭');
    }
  }, [isHistoryOpen, history.length]);
  
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  /** /health 网络失败时仅提示，不进入维护页、不卸载主界面 */
  const [healthBackendUnreachable, setHealthBackendUnreachable] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false); // 🔥 并发锁：防止重复识别
  /** 识别：spatial = Gemini+深度并行；full = 后续质心/色温等 */
  const [recognitionScanPhase, setRecognitionScanPhase] = useState<'spatial' | 'full' | null>(null);
  /** 识别后调试：Promise.all / allSettled 各腿是否成功 */
  const [recognitionSyncReport, setRecognitionSyncReport] = useState<RecognitionSyncReport | null>(null);
  const [showRecognitionSyncReport, setShowRecognitionSyncReport] = useState(false);
  const [isRendering, setIsRendering] = useState(false); // 🔥 渲染并发锁：防止重复渲染导致重复扣费
  const [renderingProgress, setRenderingProgress] = useState(0); // 🔥 侧栏渲染进度（0-100）
  const [renderingProgressSource, setRenderingProgressSource] = useState<'generate' | 'command' | null>(null);
  // 🔥 编辑模式状态：用于手动添加和移动光源点
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedLightSourceType, setSelectedLightSourceType] = useState<string | null>(null);
  const [selectedLightSourceLabel, setSelectedLightSourceLabel] = useState<string | null>(null);
  /** 手动「光带」场景（与识别 strip_context 枚举一致） */
  const [manualStripContext, setManualStripContext] = useState<string>('generic');
  /** 手动「筒灯」场景（与识别 downlight_context 枚举一致） */
  const [manualDownlightContext, setManualDownlightContext] = useState<string>('generic');
  
  // 🔥 撤销历史栈：最多保存50步
  const [undoHistory, setUndoHistory] = useState<Array<{ fixtures: Fixture[]; selectedFixtureIds: string[] }>>([]);
  const [undoHistoryIndex, setUndoHistoryIndex] = useState<number>(-1); // 当前历史索引

  /** 「全部重置」：各灯的初始快照（识别完成 / 载入历史 / 参考图结构同步后整表替换；手动加点写入单条） */
  const fixtureBaselineByIdRef = useRef<Record<string, Fixture>>({});
  /** 同一张原图是否已跑完过至少一次「识别」（换图重置；侧栏内联确认，不再用 window.confirm） */
  const [recognitionCompletedForCurrentImage, setRecognitionCompletedForCurrentImage] = useState(false);
  useEffect(() => {
    setRecognitionCompletedForCurrentImage(false);
  }, [project.originalImage]);

  const replaceFixtureBaseline = useCallback((fixtures: Fixture[]) => {
    const next: Record<string, Fixture> = {};
    for (const f of fixtures) {
      next[f.id] = JSON.parse(JSON.stringify(f)) as Fixture;
    }
    fixtureBaselineByIdRef.current = next;
  }, []);

  const handleResetFixturesToBaseline = useCallback(() => {
    setProject((prev) => ({
      ...prev,
      fixtures: prev.fixtures.map((f) => {
        const b = fixtureBaselineByIdRef.current[f.id];
        if (b) return JSON.parse(JSON.stringify(b)) as Fixture;
        return {
          ...f,
          intensity: 1.0,
          colorTemp: 0,
          hue: 0,
          saturation: 100,
          lightness: 50,
          isModified: false,
        };
      }),
      renderedImage: null,
    }));
  }, []);

  // 🔧 检测维护模式（本地开发不请求 /health，避免后端未启动时 Vite 代理 ECONNREFUSED 刷屏）
  useEffect(() => {
    const isLocalDev = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1' ||
                      window.location.port === '3000';

    if (isLocalDev) {
      setIsMaintenanceMode(false);
      setHealthBackendUnreachable(false);
      return;
    }

    const checkMaintenance = async () => {
      try {
        const apiBaseUrl = getApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/health`, { signal: AbortSignal.timeout(5000) });
        if (!response.ok) {
          setHealthBackendUnreachable(true);
          console.warn('[health] unreachable_keep_app', { status: response.status });
          return;
        }
        const data = await response.json();
        const inMaintenance = data.status === 'maintenance' || data.maintenance === true;
        if (inMaintenance) {
          console.log('[health] maintenance', data);
          setHealthBackendUnreachable(false);
          setIsMaintenanceMode(true);
        } else {
          console.log('[health] ok');
          setHealthBackendUnreachable(false);
          setIsMaintenanceMode(false);
        }
      } catch (err) {
        setHealthBackendUnreachable(true);
        console.warn(
          '[health] unreachable_keep_app',
          err instanceof Error ? err.message : err,
        );
      }
    };

    checkMaintenance();
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);

  // 🔧 如果处于维护模式，显示维护页面
  if (isMaintenanceMode) {
    return <Maintenance />;
  }

  // 与后端 image_history.expires_at（7 天）及侧栏「存储 7 天」说明一致；勿再用 24h，否则隔夜会误删仍有效的库记录
  const HISTORY_CLIENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

  const cleanExpiredHistory = (items: HistoryItem[]): HistoryItem[] => {
    const now = Date.now();

    const filtered = items.filter((item) => {
      const age = now - item.timestamp;
      return age < HISTORY_CLIENT_MAX_AGE_MS;
    });

    if (filtered.length !== items.length) {
      console.log(
        `🧹 清理过期历史记录: ${items.length} -> ${filtered.length} (删除了 ${items.length - filtered.length} 条超过 7 天的记录)`,
      );
    }

    return filtered;
  };

  /** 不含 fixture id：库往返后 id 可能变化，避免与本地指纹对不上 */
  const historyFixturesFingerprint = (fixtures: Fixture[]): string => {
    const mod = fixtures
      .filter((f) => f.isModified)
      .sort((a, b) => a.x - b.x || a.y - b.y || String(a.type).localeCompare(String(b.type)));
    return JSON.stringify(
      mod.map((f) => ({
        x: Math.round(Number(f.x) || 0),
        y: Math.round(Number(f.y) || 0),
        type: f.type,
        intensity: Math.round((Number(f.intensity) || 0) * 1000) / 1000,
        colorTemp: Math.round(Number(f.colorTemp) || 0),
        hue: Math.round(Number(f.hue) || 0),
      })),
    );
  };

  const isHttpRenderedUrl = (url: string) => /^https?:\/\//i.test(url || '');

  /** image_history 主键为 UUID；合并时若保留本地 Date.now() 临时 id，删除接口会匹配不到行 */
  const isDatabaseHistoryId = (id: string | null | undefined) =>
    typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id.trim());

  /** 同一渲染图合并本地与服务端条目时：优先保留库里的 UUID，避免删除/去重异常 */
  const pickBetterHistoryForMerge = (a: HistoryItem, b: HistoryItem): HistoryItem => {
    const sameRender = !!(a.renderedImage && a.renderedImage === b.renderedImage);
    if (sameRender) {
      const aDb = isDatabaseHistoryId(a.id);
      const bDb = isDatabaseHistoryId(b.id);
      if (aDb && !bDb) return a;
      if (!aDb && bDb) return b;
    }
    const aHttp = isHttpRenderedUrl(a.renderedImage);
    const bHttp = isHttpRenderedUrl(b.renderedImage);
    if (aHttp && !bHttp) return a;
    if (!aHttp && bHttp) return b;
    return a.timestamp >= b.timestamp ? a : b;
  };

  // 后端历史刷新时与当前本地历史合并，避免“刚生成的图”被覆盖丢失
  const mergeHistoryItems = (serverItems: HistoryItem[], localItems: HistoryItem[]): HistoryItem[] => {
    const map = new Map<string, HistoryItem>();
    [...serverItems, ...localItems].forEach((item) => {
      const key = item.renderedImage || item.id;
      const prev = map.get(key);
      if (!prev) {
        map.set(key, item);
      } else {
        map.set(key, pickBetterHistoryForMerge(prev, item));
      }
    });
    let merged = Array.from(map.values()).sort((a, b) => b.timestamp - a.timestamp);

    // 同一次生成：本地/服务端时间戳可能差数分钟；同一日历分钟也视为同批
    const DEDupe_WINDOW_MS = 10 * 60 * 1000;
    const collapsed: HistoryItem[] = [];
    for (const item of merged) {
      const fp = historyFixturesFingerprint(item.fixtures);
      const dupIdx = collapsed.findIndex((ex) => {
        if (historyFixturesFingerprint(ex.fixtures) !== fp) return false;
        // 文字命令连改：fixtures 不变但结果图不同；若仅按指纹+时间窗合并会少存多条
        if (ex.renderedImage && item.renderedImage && ex.renderedImage !== item.renderedImage) {
          return false;
        }
        const dt = Math.abs(ex.timestamp - item.timestamp);
        const sameMinute = Math.floor(ex.timestamp / 60000) === Math.floor(item.timestamp / 60000);
        return dt <= DEDupe_WINDOW_MS || sameMinute;
      });
      if (dupIdx >= 0) {
        const ex = collapsed[dupIdx];
        collapsed[dupIdx] = pickBetterHistoryForMerge(ex, item);
      } else {
        collapsed.push(item);
      }
    }
    merged = collapsed.sort((a, b) => b.timestamp - a.timestamp);

    // 数据库若误插两条相同 rendered_image_url，这里再收一道
    const urlLastKept = new Map<string, number>();
    merged = merged.filter((item) => {
      const u = item.renderedImage;
      if (!u) return true;
      const t = item.timestamp;
      const prevT = urlLastKept.get(u);
      if (prevT !== undefined && Math.abs(t - prevT) <= 5 * 60 * 1000) {
        return false;
      }
      urlLastKept.set(u, t);
      return true;
    });

    return cleanExpiredHistory(merged);
  };

  // 兜底：渲染成功后先本地立即入存档，再由后端历史刷新覆盖校准
  const appendHistoryItemLocal = (params: {
    originalImage: string;
    renderedImage: string;
    fixtures: Fixture[];
    activeFixtureId?: string | null;
  }) => {
    setHistory((prev) => {
      const cleaned = cleanExpiredHistory(prev);
      const dupIndex = cleaned.findIndex((i) => i.renderedImage === params.renderedImage);
      const existingId = dupIndex >= 0 ? cleaned[dupIndex].id : null;
      const nextItem: HistoryItem = {
        id: existingId || Date.now().toString(),
        originalImage: params.originalImage,
        renderedImage: params.renderedImage,
        fixtures: [...params.fixtures],
        activeFixtureId: params.activeFixtureId ?? null,
        timestamp: Date.now(),
      };
      const withoutDup = dupIndex >= 0 ? cleaned.filter((_, i) => i !== dupIndex) : cleaned;
      return [nextItem, ...withoutDup];
    });
  };

  const isLikelyRenderTimeoutError = (err: any): boolean => {
    const msg = String(err?.message || '').toLowerCase();
    return msg.includes('请求超时') || msg.includes('timeout') || msg.includes('abort');
  };

  const tryRecoverRenderedFromRecentHistory = async (requestStartedAt: number): Promise<HistoryItem | null> => {
    if (!(isAuthenticated && user?.email)) return null;
    try {
      const response = await api.getHistory();
      const items: HistoryItem[] = Array.isArray(response?.history) ? response.history : [];
      if (!items.length) return null;
      const latest = items[0];
      if (!latest?.renderedImage) return null;
      const ts = Number(latest.timestamp || 0);
      const recentEnough = !Number.isFinite(ts) || ts >= requestStartedAt - 15000;
      return recentEnough ? latest : null;
    } catch {
      return null;
    }
  };

  const [isUpscaleOpen, setIsUpscaleOpen] = useState(false);
  const [isLightReconstructOpen, setIsLightReconstructOpen] = useState(false);
  const [isTextReconstructOpen, setIsTextReconstructOpen] = useState(false);
  const [isPhysicalReconstructOpen, setIsPhysicalReconstructOpen] = useState(false);
  const [isToneMappingOpen, setIsToneMappingOpen] = useState(false);
  const [rhRecoveryRequest, setRhRecoveryRequest] = useState<RunningHubRecoveryRequest | null>(null);
  const [isDepthModalOpen, setIsDepthModalOpen] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [enhanceCommandDraft, setEnhanceCommandDraft] = useState('');
  // 深浅模式：从 localStorage 读取偏好并持久化
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const saved = localStorage.getItem('theme');
      return saved !== 'light'; // 'dark' 或未设置时为深色
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    } catch {}
  }, [isDarkMode]);
  // 🔤 支持的语言列表（与落地页、AuthModal 一致：仅中英文）
  const [language, setLanguage] = useState<AuthLang>(() => {
    if (typeof window === 'undefined') return 'en';
    try {
      const saved = localStorage.getItem('lang');
      if (saved && saved !== 'en' && saved !== 'zh') {
        localStorage.setItem('lang', 'en');
        return 'en';
      }
      if (saved === 'zh') return 'zh';
    } catch {
      /* ignore */
    }
    return 'en';
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<any>(null);
  /** 邮件重置密码链接进入（PASSWORD_RECOVERY / hash type=recovery）时全屏设置新密码 */
  const [showPasswordResetOverlay, setShowPasswordResetOverlay] = useState(false);

  // ============================================================================
  // 🔒 P0-A1（Sprint 1）transient session=null guard
  // 修复 5–10 分钟黑屏：Supabase token 抖动 / 临时 refresh 失败时 onAuthStateChange
  // 会 fire(_, null)，原逻辑立刻 setIsAuthenticated(false) → 主 App 返回 <AuthModal/>。
  // 现在改为：grace period（8s）后再做一次 getSession 二次确认。
  // 关键契约：grace active 时绝不再创建第二个 timer，绝不重复 clear state；
  //   主动 logout 通过 markExplicitAuthClear 立即绕过 grace（0ms 下沉）。
  // ============================================================================
  const TRANSIENT_NULL_GRACE_MS = 8000;
  const EXPLICIT_CLEAR_WINDOW_MS = 5000;
  const transientNullTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transientNullStartedAtRef = useRef<number | null>(null);
  const explicitAuthClearAtRef = useRef<number>(0);
  const isAuthenticatedRef = useRef<boolean | null>(null);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const markExplicitAuthClear = useCallback((reason: string) => {
    const now = Date.now();
    explicitAuthClearAtRef.current = now;
    try {
      localStorage.setItem('_app_explicit_auth_clear_at', String(now));
    } catch {
      /* ignore */
    }
    console.log(`[auth.explicit_clear] reason=${reason} at=${now}`);
  }, []);

  const isExplicitClearActive = useCallback((): boolean => {
    const memTs = explicitAuthClearAtRef.current;
    let lsTs = 0;
    try {
      lsTs = Number(localStorage.getItem('_app_explicit_auth_clear_at') || 0);
    } catch {
      /* ignore */
    }
    const ts = Math.max(memTs, lsTs);
    return ts > 0 && Date.now() - ts < EXPLICIT_CLEAR_WINDOW_MS;
  }, []);

  const clearTransientNullTimer = useCallback(() => {
    if (transientNullTimerRef.current !== null) {
      clearTimeout(transientNullTimerRef.current);
      transientNullTimerRef.current = null;
    }
    transientNullStartedAtRef.current = null;
  }, []);

  const readHashNorm = useCallback((): string => {
    if (typeof window === 'undefined') return '';
    return window.location.hash.replace(/^#/, '').trim().toLowerCase();
  }, []);

  /** 与地址栏 #hash 同步（含登录后恢复 #admin 的中间帧，见 NEMO_POST_LOGIN_ADMIN_KEY） */
  const [routeHashNorm, setRouteHashNorm] = useState<string>(() =>
    typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '').trim().toLowerCase() : '',
  );

  useEffect(() => {
    const sync = () => {
      const h = readHashNorm();
      setRouteHashNorm(h);
      if (h !== 'admin' && isAuthenticated === false) {
        try {
          sessionStorage.removeItem(NEMO_POST_LOGIN_ADMIN_KEY);
        } catch {
          /* ignore */
        }
      }
    };
    sync();
    window.addEventListener('hashchange', sync);
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener('hashchange', sync);
      window.removeEventListener('popstate', sync);
    };
  }, [readHashNorm, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated !== false) return;
    if (readHashNorm() !== 'admin') return;
    try {
      sessionStorage.setItem(NEMO_POST_LOGIN_ADMIN_KEY, '1');
    } catch {
      /* ignore */
    }
  }, [isAuthenticated, readHashNorm]);

  useEffect(() => {
    if (isAuthenticated !== true) return;
    if (typeof window === 'undefined') return;
    try {
      if (sessionStorage.getItem(NEMO_POST_LOGIN_ADMIN_KEY) !== '1') return;
      sessionStorage.removeItem(NEMO_POST_LOGIN_ADMIN_KEY);
      if (readHashNorm() === 'admin') {
        setRouteHashNorm('admin');
        return;
      }
      const u = new URL(window.location.href);
      u.hash = '#admin';
      window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
      setRouteHashNorm('admin');
    } catch {
      /* ignore */
    }
  }, [isAuthenticated, readHashNorm]);

  const [lightingPresets, setLightingPresets] = useState<LightingPresetRow[]>([]);
  const [selectedLightingPresetId, setSelectedLightingPresetId] = useState<string | null>(null);
  const [useLightingPresetForRender, setUseLightingPresetForRender] = useState(false);

  /** 每次上传/载入历史新建；与后端 public.tasks 绑定，每任务 2 次识别 */
  const [lightingTaskId, setLightingTaskId] = useState<string | null>(null);
  /**
   * 同一主图在未成功「生成」前：最多 2 次发起识别（第 1 次免费尝试，第 2 次为最后一次）。
   * 必须在调用 /analyze **之前** 同步 +1 并 setState，侧栏才能立刻禁用，避免异步完成前重复点击与弹窗误导。
   */
  const [lightingIdentifyCount, setLightingIdentifyCount] = useState(0);
  /** 与 lightingIdentifyCount 同步 */
  const lightingIdentifyCountRef = useRef(0);
  const lightingTaskIdRef = useRef<string | null>(null);
  /** 同步互斥：避免在 React 尚未提交 isAnalyzing 时连点触发多次识别 */
  const recognizeFixtureBusyRef = useRef(false);

  useEffect(() => {
    lightingTaskIdRef.current = lightingTaskId;
  }, [lightingTaskId]);

  const createLightingTaskForCurrentUser = useCallback(async (): Promise<string | null> => {
    if (!user?.email) {
      lightingTaskIdRef.current = null;
      setLightingTaskId(null);
      setLightingIdentifyCount(0);
      lightingIdentifyCountRef.current = 0;
      return null;
    }
    try {
      const r = (await api.createLightingTask()) as { taskId?: string; identifyCount?: number };
      if (r?.taskId) {
        const ic = Number.isFinite(Number(r.identifyCount)) ? Number(r.identifyCount) : 0;
        lightingTaskIdRef.current = r.taskId;
        setLightingTaskId(r.taskId);
        setLightingIdentifyCount(ic);
        lightingIdentifyCountRef.current = ic;
        return r.taskId;
      }
    } catch (e) {
      console.error('[任务] 创建失败:', e);
    }
    return null;
  }, [user?.email]);

  /** 付费渲染成功后新建 lighting task，恢复每图 2 次识别额度（与后端 tasks 表一致） */
  const refreshLightingTaskAfterPaidRender = useCallback(async () => {
    const ADMIN = '276517619@qq.com';
    if (!user?.email || user.email === ADMIN) return;
    await createLightingTaskForCurrentUser();
  }, [user?.email, createLightingTaskForCurrentUser]);

  const refreshLightingPresets = useCallback(async () => {
    const uid = user?.id;
    if (!uid || isAuthenticated !== true) {
      setLightingPresets([]);
      return;
    }
    const rows = await fetchUserLightingPresets(supabase, uid);
    setLightingPresets(rows);
  }, [user?.id, isAuthenticated]);

  useEffect(() => {
    void refreshLightingPresets();
  }, [refreshLightingPresets]);

  useEffect(() => {
    if (!selectedLightingPresetId) return;
    if (!lightingPresets.some((p) => p.id === selectedLightingPresetId)) {
      setSelectedLightingPresetId(null);
    }
  }, [lightingPresets, selectedLightingPresetId]);

  // 应用内切换语言时写回 localStorage，与落地页语言选择器一致
  useEffect(() => {
    localStorage.setItem('lang', language);
  }, [language]);
  // 🔥 标志：用于阻止注册时未确认邮箱的 session 被处理
  const isRegisteringRef = React.useRef(false);
  /** 并发/连续多次拉取历史时递增，用于丢弃过期响应，避免 merge 互相覆盖导致「项目存档」数量抖动 */
  const historyFetchGenRef = React.useRef(0);

  // 🔥 从数据库加载历史记录
  // 🔒🔒🔒 永久锁死代码 - 历史记录加载功能 🔒🔒🔒
  // ⚠️⚠️⚠️ 警告：此代码已被"永久锁死"，绝对禁止随意修改！
  // 功能：任何人都可以在不载入图片的情况下查看历史存档
  // 要求：即使认证失败，也要尝试调用 API，让后端决定是否返回历史记录
  // 锁死日期：2026-02-11
  // 🔒🔒🔒 未经用户明确要求，绝对禁止修改以下代码 🔒🔒🔒
  const fetchHistoryFromDatabase = async (excludeHistoryIds?: string[]) => {
    const fetchGen = ++historyFetchGenRef.current;
    console.log('[历史记录] 🚀 fetchHistoryFromDatabase 函数被调用');
    try {
      // 🔥 直接从 Supabase 获取 session，不依赖 React 状态（因为状态可能还没更新）
      let { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      // 🔥 如果 session 无效，尝试刷新 token（但不阻止 API 调用）
      if (sessionError || !session || !session.user?.email) {
        console.warn('[历史记录] ⚠️  无法获取有效的 Supabase session，尝试刷新 token...');
        if (sessionError) {
          console.warn('[历史记录] Session 错误:', sessionError.message);
        }
        
        // 🔥 尝试刷新 session
        try {
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError && refreshedSession) {
            console.log('[历史记录] ✅ Token 刷新成功');
            session = refreshedSession;
          } else {
            console.warn('[历史记录] ⚠️  Token 刷新失败:', refreshError?.message);
            console.log('[历史记录] ⚠️  但继续尝试调用 API（即使没有 token）');
            // 🔥 不返回，继续尝试调用 API
          }
        } catch (refreshError: any) {
          console.warn('[历史记录] ⚠️  Token 刷新异常:', refreshError?.message);
          console.log('[历史记录] ⚠️  但继续尝试调用 API（即使没有 token）');
          // 🔥 不返回，继续尝试调用 API
        }
      }
      
      // 🔥 无论是否有 session，都尝试调用 API（让后端决定是否返回历史记录）
      console.log('[历史记录] 准备调用 api.getHistory()...');
      console.log('[历史记录] Session 状态:', session ? '存在' : '不存在');
      console.log('[历史记录] Token 存在:', !!session?.access_token);
      
      const response = await api.getHistory();
      if (fetchGen !== historyFetchGenRef.current) {
        console.log('[历史记录] 忽略过期响应（已有更新的拉取）');
        return;
      }
      console.log('[历史记录] API 响应:', response);
      
      if (response && Array.isArray(response.history)) {
        console.log(`[历史记录] ✅ 加载成功，共 ${response.history.length} 条记录`);
        setHistory((prev) => {
          const base =
            excludeHistoryIds?.length ? prev.filter((i) => !excludeHistoryIds.includes(i.id)) : prev;
          return mergeHistoryItems(response.history, base);
        });
      } else {
        console.log('[历史记录] ⚠️  未返回历史记录数据，仅合并本地（避免 setState 返回同一引用导致界面不刷新）');
        setHistory((prev) => {
          const base =
            excludeHistoryIds?.length ? prev.filter((i) => !excludeHistoryIds.includes(i.id)) : prev;
          return mergeHistoryItems([], base);
        });
      }
    } catch (error: any) {
      console.error('[历史记录] ❌ 加载失败:', error);
      console.error('[历史记录] 错误详情:', error.message);
      console.error('[历史记录] 错误堆栈:', error.stack);
      
      // 🔥 如果是认证错误，尝试刷新 token 后重试
      if (error.message?.includes('未授权') || error.message?.includes('Unauthorized') || error.message?.includes('401')) {
        console.warn('[历史记录] ⚠️  认证失败（401），尝试刷新 token 后重试...');
        try {
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError && refreshedSession) {
            console.log('[历史记录] ✅ Token 刷新成功，重新加载历史记录...');
            // 🔥 重新尝试加载历史记录
            try {
              const response = await api.getHistory();
              if (fetchGen !== historyFetchGenRef.current) {
                console.log('[历史记录] 重试响应已过期，忽略');
                return;
              }
              if (response && Array.isArray(response.history)) {
                console.log(`[历史记录] ✅ 重试加载成功，共 ${response.history.length} 条记录`);
                setHistory((prev) => {
                  const base =
                    excludeHistoryIds?.length ? prev.filter((i) => !excludeHistoryIds.includes(i.id)) : prev;
                  return mergeHistoryItems(response.history, base);
                });
                return;
              }
            } catch (retryError: any) {
              console.error('[历史记录] ❌ 重试加载失败:', retryError);
            }
          } else {
            console.warn('[历史记录] ⚠️  Token 刷新失败:', refreshError?.message);
          }
        } catch (refreshError: any) {
          console.warn('[历史记录] ⚠️  Token 刷新异常:', refreshError?.message);
        }
        
        // 🔥 即使刷新失败，也不清空历史记录，允许打开历史面板（即使没有数据）
        console.warn('[历史记录] ⚠️  认证失败，可能是 token 过期，请刷新页面重新登录');
        console.warn('[历史记录] ⚠️  但允许打开历史面板查看（如果有缓存的数据）');
        return; // 🔥 不清空历史记录，保留之前的数据
      }
      
      // 其他错误，不清空历史记录（允许打开历史面板查看缓存的数据）
      console.warn('[历史记录] ⚠️  其他错误，不清空历史记录（允许打开历史面板查看缓存的数据）');
      // 🔥 不清空历史记录，保留之前的数据
    }
  };
  
  const [showPaymentCenter, setShowPaymentCenter] = useState(false);
  const [autoRechargePlanId, setAutoRechargePlanId] = useState<RechargePlanId | null>(null);
  const [returnUrlOverride, setReturnUrlOverride] = useState<string | null>(null);
  const [showUserAccount, setShowUserAccount] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // 移动端侧边栏显示状态

  /**
   * 历史记录/外链图片兼容：
   * - 渲染链路需要 data:image/...;base64,... 格式
   * - 但历史记录里可能是 http(s) URL / blob URL / 纯 base64
   */
  const ensureImageDataUrl = useCallback(async (image: string): Promise<string> => {
    if (!image || typeof image !== 'string') throw new Error('图片数据无效');
    const trimmed = image.trim();
    if (/^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(trimmed)) return trimmed;

    // http(s)/blob: URL -> fetch -> blob -> dataURL
    if (/^(https?:\/\/|blob:)/i.test(trimmed)) {
      // 先尝试浏览器直连（同源/已允许 CORS 的情况）
      try {
        const res = await fetch(trimmed);
        if (!res.ok) throw new Error(`图片加载失败 (${res.status})`);
        const blob = await res.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(new Error('图片读取失败'));
          reader.readAsDataURL(blob);
        });
        if (!/^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(dataUrl)) {
          throw new Error('图片格式无效');
        }
        return dataUrl;
      } catch (e) {
        // 直连失败（常见：COS 跨域/CORS），改走后端代理拉取
        const resp = await api.fetchImageDataUrl(trimmed);
        const dataUrl = (resp as any)?.dataUrl as string | undefined;
        if (dataUrl && /^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(dataUrl)) return dataUrl;
        throw e;
      }
    }

    // 纯 base64（无前缀）-> 兜底加 PNG 前缀（后端/前端会再次校验与修复）
    if (trimmed.length > 100 && !trimmed.includes('data:')) {
      return `data:image/png;base64,${trimmed}`;
    }

    throw new Error('图片格式无效');
  }, []);

  const handleOpenPhotopea = useCallback(async () => {
    const raw = project.renderedImage;
    if (!raw || !String(raw).trim()) {
      alert(language !== 'zh' ? 'Generate an image first, then open Photopea.' : '请先完成渲染生成图片，再打开 Photopea。');
      return;
    }
    try {
      let publicUrl: string;
      const s = String(raw).trim();
      if (isTencentCosPublicUrl(s)) {
        publicUrl = s.split('?')[0];
      } else {
        if (isAuthenticated !== true) {
          alert(
            language !== 'zh'
              ? 'Please log in so the image can be uploaded for a public link (Photopea needs HTTPS URL).'
              : '请登录后将当前图上传至云端以生成公网链接，供 Photopea 打开。',
          );
          return;
        }
        const dataUrl = await ensureImageDataUrl(s);
        const resp = (await api.uploadImageForPhotopea(dataUrl)) as {
          url?: string;
          error?: string;
          message?: string;
        };
        if (!resp?.url || typeof resp.url !== 'string') {
          throw new Error(resp?.message || resp?.error || 'NO_URL');
        }
        publicUrl = resp.url;
      }
      const target = buildPhotopeaOpenUrl([publicUrl]);
      window.open(target, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      const msg = e?.message || String(e);
      alert(
        language !== 'zh'
          ? `Could not open Photopea with this image. If the file is on COS, allow Origin https://www.photopea.com in bucket CORS. ${msg}`
          : `无法在 Photopea 中打开当前图片。若使用 COS 链接，请在存储桶「跨域访问 CORS」中允许来源 https://www.photopea.com。${msg}`,
      );
    }
  }, [project.renderedImage, language, ensureImageDataUrl, isAuthenticated]);

  // 侧栏积分：GET /api/credits/account（credits_ledger，与账户中心一致）
  const ensurePublicUserOnLogin = useCallback(async () => {
    try {
      await api.ensurePublicUser();
    } catch (e: unknown) {
      console.warn('[ensure-public-user]', e instanceof Error ? e.message : e);
    }
  }, []);

  const refreshAccountRef = useRef(refreshAccount);
  refreshAccountRef.current = refreshAccount;

  /** 切回页面 / 窗口 focus 时刷新账户积分 */
  useEffect(() => {
    if (!user?.email) return;

    const run = () => {
      void refreshAccountRef.current({ silent: true, reason: 'window_focus_or_visibility' });
    };

    const onVis = () => {
      if (document.visibilityState === 'visible') run();
    };
    window.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', run);

    return () => {
      window.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', run);
    };
  }, [user?.email]);

  // 检查用户认证状态（若邮件为「本站 ?token_hash=…」须先 verifyOtp，再拉 session）
  useEffect(() => {
    let cancelled = false;
    let authSubscription: { unsubscribe: () => void } | null = null;

    void (async () => {
      const langZh =
        typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('zh');
      await consumeRecoveryQueryIfPresent(langZh);
      if (cancelled) return;

      supabase.auth.getSession().then(async ({ data: { session } }) => {
      try {
      const rawHash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
      const entryHash = rawHash.toLowerCase();
      const hashParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.hash.replace(/^#/, '')) : null;
      const recoveryHinted = peekPasswordRecoveryHint();
      const isPasswordRecoveryUrl =
        hashParams?.get('type') === 'recovery' || recoveryHinted;
      const fromLandingLoginLink = entryHash === 'login';
      // 已登录用户点「登录」：直接进入实验室并去掉 hash。#signup 保留 hash，由已登录分支在界面上叠加注册弹窗
      if (fromLandingLoginLink && session?.user) {
        const confirmed =
          session.user.email_confirmed_at != null ||
          (session.user as any).confirmed_at != null ||
          (session.user as any).email_verified === true;
        if (confirmed) {
          console.log('[Auth] 已登录用户从官网 #login 进入：保留会话，进入实验室');
          window.history.replaceState({}, '', window.location.pathname);
        }
      }

      // 🔥 先验证邮箱确认状态，再设置认证状态（避免未确认用户导致弹窗闪烁）
      if (session?.user) {
        // 注意：使用 != null 同时检查 null 和 undefined
        const isEmailConfirmed = (session.user.email_confirmed_at != null) || 
                                 ((session.user as any).confirmed_at != null) ||
                                 ((session.user as any).email_verified === true);
        
        console.log('🔍 [初始检查] 邮箱确认状态:', {
          email: session.user.email,
          email_confirmed_at: session.user.email_confirmed_at,
          confirmed_at: (session.user as any).confirmed_at,
          email_verified: (session.user as any).email_verified,
          isEmailConfirmed
        });
        
        if (!isEmailConfirmed) {
          // 🔥 邮箱未确认，立即设置未认证状态（不等待 signOut）
          console.log('🔒 [初始检查] 检测到邮箱未确认的用户，立即清除 session');
          
          // 立即清除本地存储（同步操作）
          try {
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith('sb-') || key.includes('supabase')) {
                localStorage.removeItem(key);
              }
            });
            Object.keys(sessionStorage).forEach(key => {
              if (key.startsWith('sb-') || key.includes('supabase')) {
                sessionStorage.removeItem(key);
              }
            });
            console.log('🧹 [初始检查] 已清除本地存储');
          } catch (storageError) {
            console.warn('⚠️ [初始检查] 清除本地存储失败:', storageError);
          }
          
          // 立即设置未认证状态（不等待 signOut 完成）
          setIsAuthenticated(false);
          setUser(null);
          invalidateAccount();
          
          // 异步清除远程 session（不等待）
          supabase.auth.signOut().then(() => {
            console.log('✅ [初始检查] 远程 Session 已清除');
          }).catch((error) => {
            console.warn('⚠️ [初始检查] 清除远程 session 失败:', error);
          });
          
          return; // 🔥 重要：直接返回，不设置 isAuthenticated
        }
      }
      
      // 🔥 只有邮箱已确认（或没有 session）才设置认证状态
      setIsAuthenticated(!!session);
      setUser(session?.user ?? null);
      if (session?.user && isPasswordRecoveryUrl) {
        clearPasswordRecoveryHint();
      } else if (!session?.user && recoveryHinted) {
        clearPasswordRecoveryHint();
      }
      setShowPasswordResetOverlay(Boolean(session?.user && isPasswordRecoveryUrl));
      
      // ============================================================================
      // 🔒🔒🔒 永久锁死代码 - 初始化开发者工具保护 🔒🔒🔒
      // ============================================================================
      // ⚠️⚠️⚠️ 绝对禁止删除或修改此处的调用！
      // 功能：根据用户邮箱初始化开发者工具保护，管理员（276517619@qq.com）可以打开 F12
      // 此调用必须使用 session?.user?.email，不能使用其他值
      // 锁死日期：2026-02-08
      // 🔒🔒🔒 即使修改其他代码，也不能删除或修改这个调用 🔒🔒🔒
      // ============================================================================
      // 🔒 初始化开发者工具保护（根据用户邮箱）
      initDevToolsProtection(session?.user?.email ?? null);
      
      // 如果用户已登录且邮箱已确认，获取账户信息和历史记录
      if (session?.user?.email && !isPasswordRecoveryUrl) {
        console.log('[页面加载] ✅ 用户已登录，开始加载账户信息和历史记录');
        void ensurePublicUserOnLogin().then(() =>
          refreshAccount({ silent: true, reason: 'auth_session_ready' }),
        );
        // 🔥 页面刷新时也加载历史记录（延迟执行，确保 session 完全建立）
        setTimeout(() => {
          console.log('[页面加载] 🔄 延迟500ms后调用 fetchHistoryFromDatabase');
          fetchHistoryFromDatabase();
        }, 500);
      } else if (!session?.user?.email) {
        // 如果用户未登录，清空历史记录
        console.log('[页面加载] ⚠️ 用户未登录，清空历史记录');
        setHistory([]);
      }
      } catch (initErr) {
        console.error('[初始检查] 异常:', initErr);
        setIsAuthenticated(false);
        setUser(null);
      }
    });

    // 监听认证状态变化
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔔 [认证状态变化]', { event, hasSession: !!session, userEmail: session?.user?.email });

      let hashRecovery = false;
      try {
        if (typeof window !== 'undefined') {
          hashRecovery = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('type') === 'recovery';
        }
      } catch {
        hashRecovery = false;
      }
      const hintRecovery = peekPasswordRecoveryHint();
      const inPasswordRecovery =
        event === 'PASSWORD_RECOVERY' ||
        ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && (hashRecovery || hintRecovery));
      if (inPasswordRecovery && session?.user) {
        setShowPasswordResetOverlay(true);
      }
      if (event === 'SIGNED_OUT') {
        clearPasswordRecoveryHint();
        setShowPasswordResetOverlay(false);
      }
      
      // 🔥 检查是否应该忽略这次状态变化（注册时的情况，无论邮箱是否确认）
      // 因为注册时用户还没有输入验证码，不能直接登录
      const shouldIgnore = localStorage.getItem('_supabase_ignore_signup_session') === 'true';
      if (shouldIgnore && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        // P0-A1: 注册期间的 ignore-session 也是主动 clear，绕过 grace
        markExplicitAuthClear('signup_session_ignored');
        console.log('🔒 [认证] 检测到注册时的 session，忽略这次状态变化（用户还未输入验证码）');
        console.log('   事件类型:', event);
        console.log('   用户邮箱:', session?.user?.email);
        
        // 立即清除本地存储（同步操作）
        try {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-') || key.includes('supabase')) {
              localStorage.removeItem(key);
            }
          });
          Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith('sb-') || key.includes('supabase')) {
              sessionStorage.removeItem(key);
            }
          });
          console.log('🧹 [认证] 已清除本地存储');
        } catch (storageError) {
          console.warn('⚠️ [认证] 清除本地存储失败:', storageError);
        }
        
        // 立即设置未认证状态（不等待 signOut）
        setIsAuthenticated(false);
        setUser(null);
        invalidateAccount();
        
        // 异步清除远程 session（不等待）
        if (session) {
          supabase.auth.signOut().catch(() => {});
        }
        return; // 直接返回，不处理
      }
      
      // 🔥 先验证邮箱确认状态，再设置认证状态（避免未确认用户导致弹窗闪烁）
      if (session?.user) {
        // 注意：使用 != null 同时检查 null 和 undefined
        const isEmailConfirmed = (session.user.email_confirmed_at != null) || 
                                 ((session.user as any).confirmed_at != null) ||
                                 ((session.user as any).email_verified === true);
        
        console.log('🔍 [认证状态变化] 邮箱确认检查:', {
          email: session.user.email,
          email_confirmed_at: session.user.email_confirmed_at,
          confirmed_at: (session.user as any).confirmed_at,
          email_verified: (session.user as any).email_verified,
          isEmailConfirmed,
          event
        });
        
        if (!isEmailConfirmed) {
          // P0-A1: 邮箱未确认强制 signOut 也是主动 clear，绕过 transient grace
          markExplicitAuthClear('email_not_confirmed_listener_branch');
          // 🔥 邮箱未确认，立即清除 session，要求用户先确认邮箱
          console.log('🔒 [认证] ⚠️ 检测到邮箱未确认的用户，立即清除 session');
          console.log('   事件类型:', event);
          console.log('   用户邮箱:', session.user.email);
          
          // 🔥 立即清除本地存储（同步操作，立即生效，阻止后续读取）
          try {
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith('sb-') || key.includes('supabase')) {
                localStorage.removeItem(key);
              }
            });
            console.log('🧹 [认证] 已清除本地存储的 session（同步完成）');
          } catch (storageError) {
            console.warn('⚠️ [认证] 清除本地存储失败:', storageError);
          }
          
          // 🔥 立即设置未认证状态（不等待 signOut 完成）
          setIsAuthenticated(false);
          setUser(null);
          invalidateAccount();
          
          // 清除远程 session（异步，不等待）
          supabase.auth.signOut().then(() => {
            console.log('✅ [认证] 远程 Session 已清除');
          }).catch((error) => {
            console.error('❌ [认证] 清除远程 session 失败:', error);
            // 即使清除失败，状态已设置为未认证
          });
          
          return; // 🔥 重要：直接返回，不设置 isAuthenticated
        }
      }
      
      // ============================================================================
      // 🔒 P0-A1 transient session=null guard
      // 契约：grace active 期间，唯一动作 = 打 debounce 日志 + return。
      // 物理保证：不并发 setTimeout，不重复 clear state，不重复 invalidateAccount / dispatch。
      // ============================================================================
      const sessionIsNull = !session || !session.user;
      const explicitClear = event === 'SIGNED_OUT' && isExplicitClearActive();

      if (sessionIsNull && !explicitClear) {
        // -------- Guard #1: 单 timer 强制 debounce --------
        if (transientNullTimerRef.current !== null) {
          const startedAt = transientNullStartedAtRef.current ?? Date.now();
          const elapsedMs = Date.now() - startedAt;
          console.log('[auth.transient] grace already active, debounce', {
            event,
            hasSession: !!session,
            elapsedMs,
            graceMs: TRANSIENT_NULL_GRACE_MS,
          });
          return;
        }

        // -------- Guard #2: 已经登出态，无需 grace --------
        if (!isAuthenticatedRef.current) {
          console.log('[auth.transient] already unauthenticated, skip grace', {
            event,
            hasSession: !!session,
          });
          setIsAuthenticated(false);
          setUser(null);
          return;
        }

        // -------- Guard #3: 首个 transient null — 唯一一次创建 timer --------
        // 关键契约（修订）：grace active 期间 transientNullTimerRef 必须保持非 null，
        //   让所有后续 session=null 事件被 Guard #1 debounce。
        //   只有在 recovered / confirmed_logout / catch / cancelled 真正终结时才清 ref；
        //   且回调内最终清理必须先校验 handle 身份（防止 cancelled 后旧回调误清新 grace）。
        const enteredAt = Date.now();
        transientNullStartedAtRef.current = enteredAt;
        console.warn('[auth.transient] session=null detected, entering grace', {
          event,
          hasSession: !!session,
          graceMs: TRANSIENT_NULL_GRACE_MS,
          startedAt: enteredAt,
        });

        const myTimerHandle: ReturnType<typeof setTimeout> = setTimeout(async () => {
          // 注意：此处 *不* 提前清 ref。grace 全程 ref 保持非 null，
          // 让 await 期间 Guard #1 仍然 debounce 新进 session=null 事件。
          let elapsedMs = 0;
          try {
            const startedAtSnapshot = transientNullStartedAtRef.current ?? enteredAt;
            const { data, error } = await supabase.auth.getSession();
            if (error) {
              console.warn('[auth.transient] retry getSession error', error.message);
            }
            elapsedMs = Date.now() - startedAtSnapshot;

            // 身份校验：在 await 期间是否被 transient.cancelled 抢占（ref 被改写为 null 或新 handle）
            if (transientNullTimerRef.current !== myTimerHandle) {
              console.log('[auth.transient] callback superseded, skip state change', {
                elapsedMs,
                supersededBy:
                  transientNullTimerRef.current === null ? 'cancelled' : 'newer_grace',
              });
              return;
            }

            const retrySession = data?.session ?? null;
            if (retrySession?.user) {
              console.log('[auth.transient.recovered] session restored after grace', {
                email: retrySession.user.email,
                elapsedMs,
              });
            } else {
              console.warn('[auth.transient.confirmed_logout] session still null, sinking', {
                elapsedMs,
              });
              setIsAuthenticated(false);
              setUser(null);
              invalidateAccount();
              setHistory([]);
              dispatchRunningHubHistoryReset();
            }
          } catch (e: any) {
            console.error('[auth.transient] grace retry exception', e?.message || e);
            if (transientNullTimerRef.current === myTimerHandle) {
              setIsAuthenticated(false);
              setUser(null);
            }
          } finally {
            // 终结清理：仅当 ref 仍指向 my handle 时清；被 cancelled / 被新 grace 取代则不动
            if (transientNullTimerRef.current === myTimerHandle) {
              transientNullTimerRef.current = null;
              transientNullStartedAtRef.current = null;
            }
          }
        }, TRANSIENT_NULL_GRACE_MS);
        transientNullTimerRef.current = myTimerHandle;
        return;
      }

      // -------- 非 transient（session 有效 / 主动登出）：抢占清理 grace --------
      if (transientNullTimerRef.current !== null) {
        const startedAt = transientNullStartedAtRef.current ?? Date.now();
        const elapsedMs = Date.now() - startedAt;
        console.log('[auth.transient.cancelled] new authoritative event arrived, clearing grace', {
          event,
          hasSession: !!session,
          explicitClear,
          elapsedMs,
        });
        clearTimeout(transientNullTimerRef.current);
        transientNullTimerRef.current = null;
        transientNullStartedAtRef.current = null;
      }

      // 🔥 只有邮箱已确认（或没有 session）才设置认证状态
      setIsAuthenticated(!!session);
      setUser(session?.user ?? null);
      
      // ============================================================================
      // 🔒🔒🔒 永久锁死代码 - 初始化开发者工具保护 🔒🔒🔒
      // ============================================================================
      // ⚠️⚠️⚠️ 绝对禁止删除或修改此处的调用！
      // 功能：根据用户邮箱初始化开发者工具保护，管理员（276517619@qq.com）可以打开 F12
      // 此调用必须使用 session?.user?.email，不能使用其他值
      // 锁死日期：2026-02-08
      // 🔒🔒🔒 即使修改其他代码，也不能删除或修改这个调用 🔒🔒🔒
      // ============================================================================
      // 🔒 初始化开发者工具保护（根据用户邮箱）
      initDevToolsProtection(session?.user?.email ?? null);
      
      // 如果用户已登录且邮箱已确认，获取账户信息和历史记录（重置密码流程中不预拉历史，减少闪烁）
      if (session?.user?.email && !inPasswordRecovery) {
        void ensurePublicUserOnLogin().then(() =>
          refreshAccount({ silent: true, reason: 'auth_session_ready' }),
        );
        // 🔥 从数据库加载历史记录（延迟执行，确保 session 和状态已更新）
        setTimeout(() => {
          fetchHistoryFromDatabase();
        }, 500); // 增加延迟时间，确保 session 完全建立
      } else if (!session?.user?.email) {
        // 如果用户退出登录（已通过 explicitClear 闸门 / transient.cancelled），清空账户信息和历史记录
        invalidateAccount();
        setHistory([]); // 清空历史记录
        dispatchRunningHubHistoryReset();
      }
    });
      authSubscription = authListener.subscription;
    })();

    return () => {
      cancelled = true;
      authSubscription?.unsubscribe();
    };
  }, []);

  // 🔥 独立的效果：当用户登录状态变化时，自动加载历史记录
  useEffect(() => {
    console.log('[历史记录自动加载] useEffect 触发，isAuthenticated:', isAuthenticated, 'user:', user?.email);
    
    // 🔥 双重检查：既检查 React 状态，也直接检查 Supabase session
    const checkAndLoadHistory = async () => {
      // 先检查 React 状态
      if (isAuthenticated && user?.email) {
        console.log('[历史记录自动加载] ✅ React 状态显示用户已登录');
        const timer = setTimeout(() => {
          console.log('[历史记录自动加载] 开始调用 fetchHistoryFromDatabase');
          fetchHistoryFromDatabase();
        }, 1000);
        return () => clearTimeout(timer);
      }
      
      // 如果 React 状态显示未登录，再直接检查 Supabase session（防止状态不同步）
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.email) {
          console.log('[历史记录自动加载] ✅ 直接检查 Supabase session，发现用户已登录');
          const timer = setTimeout(() => {
            console.log('[历史记录自动加载] 开始调用 fetchHistoryFromDatabase（从 session 检查）');
            fetchHistoryFromDatabase();
          }, 1500);
          return () => clearTimeout(timer);
        }
        // 不在此处 setHistory([])：与 INITIAL_SESSION 竞态时会把已登录用户的列表误清空；退出登录由 onAuthStateChange 统一清空
      } catch (error) {
        console.error('[历史记录自动加载] ❌ 检查 session 失败:', error);
      }
    };
    
    checkAndLoadHistory();
  }, [isAuthenticated, user?.email]); // 依赖 isAuthenticated 和 user.email

  const prevRunningHubUserEmailRef = useRef<string | null>(null);
  const fetchHistoryFromDatabaseRef = useRef(fetchHistoryFromDatabase);
  fetchHistoryFromDatabaseRef.current = fetchHistoryFromDatabase;

  useEffect(() => {
    const email = user?.email?.trim().toLowerCase() || null;
    if (prevRunningHubUserEmailRef.current !== null && prevRunningHubUserEmailRef.current !== email) {
      console.log('[App] account switch reload', {
        from: prevRunningHubUserEmailRef.current,
        to: email,
      });
      dispatchRunningHubHistoryReload();
      void refreshAccountRef.current({ silent: true, reason: 'account_switch' });
      fetchHistoryFromDatabaseRef.current();
    }
    prevRunningHubUserEmailRef.current = email;
  }, [user?.email]);

  // Open recharge center from official landing (?open_payment_center / ?recharge_plan).
  // 未登录时 PaymentCenter 无法挂载（需要 user.email），故先写入 sessionStorage，登录成功后再打开。
  useEffect(() => {
    const url = new URL(window.location.href);
    const urlParams = url.searchParams;

    const paymentSuccess = urlParams.get('payment_success');
    if (paymentSuccess === 'true') return;

    const openPaymentCenter = urlParams.get('open_payment_center');
    const rechargePlan = urlParams.get('recharge_plan');
    const override = urlParams.get('return_url_override');
    if (!openPaymentCenter && !rechargePlan) return;

    const allowed: RechargePlanId[] = ['flex', 'starter', 'pro', 'flagship', 'yearly'];
    const plan =
      rechargePlan && allowed.includes(rechargePlan as RechargePlanId)
        ? (rechargePlan as RechargePlanId)
        : null;

    try {
      sessionStorage.setItem(
        PENDING_PAYMENT_FROM_LANDING_KEY,
        JSON.stringify({ rechargePlan: plan, returnUrlOverride: override })
      );
    } catch (_) {}

    urlParams.delete('open_payment_center');
    urlParams.delete('recharge_plan');
    urlParams.delete('return_url_override');
    const newSearch = urlParams.toString();
    const hashKeep = url.hash || '';
    window.history.replaceState({}, '', `${url.pathname}${newSearch ? `?${newSearch}` : ''}${hashKeep}`);

    const applyPendingFromStorage = () => {
      try {
        const raw = sessionStorage.getItem(PENDING_PAYMENT_FROM_LANDING_KEY);
        if (!raw) return;
        const p = JSON.parse(raw) as { rechargePlan?: string | null; returnUrlOverride?: string | null };
        if (p.rechargePlan && allowed.includes(p.rechargePlan as RechargePlanId)) {
          setAutoRechargePlanId(p.rechargePlan as RechargePlanId);
        } else {
          setAutoRechargePlanId(null);
        }
        setReturnUrlOverride(p.returnUrlOverride ?? null);
        setShowPaymentCenter(true);
        sessionStorage.removeItem(PENDING_PAYMENT_FROM_LANDING_KEY);
      } catch {
        sessionStorage.removeItem(PENDING_PAYMENT_FROM_LANDING_KEY);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user?.email) return;
      const confirmed =
        session.user.email_confirmed_at != null ||
        (session.user as any).confirmed_at != null ||
        (session.user as any).email_verified === true;
      if (!confirmed) return;
      applyPendingFromStorage();
    });
  }, []);

  // 从官网点充值进入时若未登录：先登录，会话就绪后根据 sessionStorage 打开充值中心
  useEffect(() => {
    if (!isAuthenticated || !user?.email) return;
    try {
      const raw = sessionStorage.getItem(PENDING_PAYMENT_FROM_LANDING_KEY);
      if (!raw) return;
      const p = JSON.parse(raw) as { rechargePlan?: string | null; returnUrlOverride?: string | null };
      const allowed: RechargePlanId[] = ['flex', 'starter', 'pro', 'flagship', 'yearly'];
      if (p.rechargePlan && allowed.includes(p.rechargePlan as RechargePlanId)) {
        setAutoRechargePlanId(p.rechargePlan as RechargePlanId);
      } else {
        setAutoRechargePlanId(null);
      }
      setReturnUrlOverride(p.returnUrlOverride ?? null);
      setShowPaymentCenter(true);
      sessionStorage.removeItem(PENDING_PAYMENT_FROM_LANDING_KEY);
    } catch {
      sessionStorage.removeItem(PENDING_PAYMENT_FROM_LANDING_KEY);
    }
  }, [isAuthenticated, user?.email]);

  // 检查支付成功后的返回提示（同一 URL 只处理一次；不依赖 showUserAccount）
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const orderId = urlParams.get('order_id') || '';
    const amount = urlParams.get('amount') || '';

    if (paymentSuccess !== 'true') return;

    const signature = `payment_success|${orderId}|${amount}`;
    if (handledPaymentSuccessRef.current === signature) return;
    handledPaymentSuccessRef.current = signature;

    setShowPaymentCenter(false);
    setAutoRechargePlanId(null);
    setReturnUrlOverride(null);
    console.log('💰 检测到支付成功返回:', { paymentSuccess, orderId, amount });

    if (user?.email) {
      console.log('🔄 支付成功后立即刷新账户信息...');
      void refreshAccount({ reason: 'payment_success_initial' });

      let pollCount = 0;
      const maxPolls = 15;
      let lastCredits = 0;
      const pollInterval = setInterval(async () => {
        pollCount++;
        console.log(`🔄 轮询查询积分 (${pollCount}/${maxPolls})...`);

        try {
          const credits = await refreshAccountRef.current({
            silent: true,
            reason: 'payment_success_poll',
          });
          if (credits > lastCredits) {
            console.log('✅ 轮询检测到积分变化，停止轮询');
            clearInterval(pollInterval);
          }
          lastCredits = credits;
        } catch (err) {
          console.error('轮询查询失败:', err);
        }

        if (pollCount >= maxPolls) {
          console.log('⏰ 轮询已达到最大次数，停止轮询');
          clearInterval(pollInterval);
        }
      }, 2000);

      setTimeout(() => {
        clearInterval(pollInterval);
      }, 30000);
    }

    const successMessage = amount
      ? language !== 'zh'
        ? `Payment successful! Recharged ¥${amount}, please check your account.`
        : `支付成功！已充值 ¥${amount}，请查看账户。`
      : language !== 'zh'
        ? 'Payment successful! Please check your account.'
        : '支付成功！请查看账户。';

    setPaymentMessage(successMessage);

    if (user?.email) {
      setTimeout(
        () => void refreshAccountRef.current({ silent: true, reason: 'payment_success_delayed_1s' }),
        1000,
      );
      setTimeout(
        () => void refreshAccountRef.current({ silent: true, reason: 'payment_success_delayed_2_5s' }),
        2500,
      );
      setTimeout(
        () => void refreshAccountRef.current({ silent: true, reason: 'payment_success_delayed_5s' }),
        5000,
      );
    }

    if (user?.email) {
      setTimeout(() => {
        const u = new URL(window.location.href);
        u.searchParams.delete('payment_success');
        u.searchParams.delete('order_id');
        u.searchParams.delete('amount');
        const qs = u.searchParams.toString();
        const tail = `${qs ? `?${qs}` : ''}${u.hash || ''}`;
        window.history.replaceState({}, '', `${u.pathname}${tail}`);
        setRouteHashNorm(u.hash.replace(/^#/, '').trim().toLowerCase());
        console.log('🧹 已清除支付相关 URL 参数（保留 hash）');
      }, 1000);
    }

    setTimeout(() => {
      setPaymentMessage(null);
    }, 8000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, user?.email]);

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  // 🔥 添加 ESC 键退出编辑模式的功能
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 按 ESC 键退出编辑模式
      if (e.key === 'Escape' && isEditMode) {
        setIsEditMode(false);
        setSelectedLightSourceType(null);
        console.log('✅ 按 ESC 键退出编辑模式');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditMode]);

  type AnalysisCoordinateMeta = {
    base64Width: number;
    base64Height: number;
    offsetX: number;
    offsetY: number;
    scale: number;
    originalWidth: number;
    originalHeight: number;
  };

  const buildAnalysisCoordinateMeta = (
    originalWidth: number,
    originalHeight: number,
    base64Width: number,
    base64Height: number,
    offsetX = 0,
    offsetY = 0
  ): AnalysisCoordinateMeta => {
    const contentWidth = Math.max(1, base64Width - offsetX * 2);
    const contentHeight = Math.max(1, base64Height - offsetY * 2);
    const scale = Math.min(
      contentWidth / Math.max(1, originalWidth),
      contentHeight / Math.max(1, originalHeight)
    );
    return {
      base64Width,
      base64Height,
      offsetX,
      offsetY,
      scale: Math.max(1e-6, scale),
      originalWidth,
      originalHeight,
    };
  };

  const mapModelNormalizedToOriginalPixel = (
    modelX: number,
    modelY: number,
    meta: AnalysisCoordinateMeta
  ): { x: number; y: number } => {
    // 严格按数学公式回推（禁止经验偏移）：
    // RealX = (NormalizedX / 1000 * Base64Width) - OffsetX
    // FinalX = RealX / Scale
    const realX = (Number(modelX) / 1000) * meta.base64Width - meta.offsetX;
    const realY = (Number(modelY) / 1000) * meta.base64Height - meta.offsetY;

    const originalX = realX / meta.scale;
    const originalY = realY / meta.scale;

    return {
      x: Math.max(0, Math.min(meta.originalWidth, Math.round(originalX))),
      y: Math.max(0, Math.min(meta.originalHeight, Math.round(originalY))),
    };
  };

  const mapModelNormalizedToOriginalNormalized = (
    modelX: number,
    modelY: number,
    meta: AnalysisCoordinateMeta
  ): { x: number; y: number } => {
    const pixel = mapModelNormalizedToOriginalPixel(modelX, modelY, meta);
    return {
      x: Math.max(0, Math.min(1000, Math.round((pixel.x / Math.max(1, meta.originalWidth)) * 1000))),
      y: Math.max(0, Math.min(1000, Math.round((pixel.y / Math.max(1, meta.originalHeight)) * 1000))),
    };
  };

  const mapOriginalPixelToModelNormalized = (
    originalX: number,
    originalY: number,
    meta: AnalysisCoordinateMeta
  ): { x: number; y: number } => {
    // 正向映射（与 mapModelNormalizedToOriginalPixel 严格互逆）：
    // Base64X = OriginalX * Scale + OffsetX
    // NormalizedX = Base64X / Base64Width * 1000
    const base64X = Number(originalX) * meta.scale + meta.offsetX;
    const base64Y = Number(originalY) * meta.scale + meta.offsetY;
    return {
      x: Math.max(0, Math.min(1000, Math.round((base64X / Math.max(1, meta.base64Width)) * 1000))),
      y: Math.max(0, Math.min(1000, Math.round((base64Y / Math.max(1, meta.base64Height)) * 1000))),
    };
  };

  const buildDefaultSquareCoordinateMeta = (originalWidth: number, originalHeight: number): AnalysisCoordinateMeta => {
    const targetSize = 1024;
    const scale = Math.min(targetSize / Math.max(1, originalWidth), targetSize / Math.max(1, originalHeight));
    const scaledWidth = Math.max(1, originalWidth * scale);
    const scaledHeight = Math.max(1, originalHeight * scale);
    const offsetX = (targetSize - scaledWidth) / 2;
    const offsetY = (targetSize - scaledHeight) / 2;
    return buildAnalysisCoordinateMeta(originalWidth, originalHeight, targetSize, targetSize, offsetX, offsetY);
  };

  const refineFixtureAnchorsByLuminance = async (
    sourceImageDataUrl: string,
    fixtures: Array<{ x: number; y: number; type?: string }>
  ): Promise<Array<{ x: number; y: number }>> => {
    if (!sourceImageDataUrl || !Array.isArray(fixtures) || fixtures.length === 0) {
      return fixtures.map((f) => ({ x: Number(f.x) || 0, y: Number(f.y) || 0 }));
    }

    try {
      const img = new Image();
      img.src = sourceImageDataUrl;
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => reject(new Error('Refine anchor image load timeout')), 6000);
        img.onload = () => {
          clearTimeout(timeoutId);
          resolve();
        };
        img.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error('Refine anchor image load failed'));
        };
      });

      const naturalW = Math.max(1, img.naturalWidth || 1);
      const naturalH = Math.max(1, img.naturalHeight || 1);
      const targetMax = 1200;
      const scale = Math.min(1, targetMax / Math.max(naturalW, naturalH));
      const sampleW = Math.max(1, Math.round(naturalW * scale));
      const sampleH = Math.max(1, Math.round(naturalH * scale));

      const canvas = document.createElement('canvas');
      canvas.width = sampleW;
      canvas.height = sampleH;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        return fixtures.map((f) => ({ x: Number(f.x) || 0, y: Number(f.y) || 0 }));
      }

      ctx.drawImage(img, 0, 0, sampleW, sampleH);
      const { data } = ctx.getImageData(0, 0, sampleW, sampleH);

      const refined = fixtures.map((f) => {
        const fixtureType = String(f.type || '').toLowerCase();
        const isAmbientLike = ['ambient_light', 'ambient', 'window', 'window_light', 'natural_light', 'sunlight', 'other'].includes(fixtureType);
        const isLinearLike = ['cove_light', 'cove_lighting'].includes(fixtureType);
        const isStrictEmitter = !isAmbientLike && !isLinearLike;

        const ox = Math.max(0, Math.min(naturalW, Number(f.x) || 0));
        const oy = Math.max(0, Math.min(naturalH, Number(f.y) || 0));

        const sx = Math.max(0, Math.min(sampleW - 1, Math.round(ox * scale)));
        const sy = Math.max(0, Math.min(sampleH - 1, Math.round(oy * scale)));

        // 按灯具类型设置局部搜索半径，环境光类仅做极小范围修正，避免吸附到无关亮区
        const radiusBase = isAmbientLike ? 10 : isLinearLike ? 12 : 24;
        const sampleRadius = Math.max(6, Math.round(radiusBase * Math.max(0.6, scale)));

        // 环境光类默认不做二次吸附（只保留原映射）
        if (isAmbientLike) {
          return { x: ox, y: oy };
        }

        let maxL = 0;
        for (let yy = Math.max(0, sy - sampleRadius); yy <= Math.min(sampleH - 1, sy + sampleRadius); yy++) {
          for (let xx = Math.max(0, sx - sampleRadius); xx <= Math.min(sampleW - 1, sx + sampleRadius); xx++) {
            const idx = (yy * sampleW + xx) * 4;
            const rr = data[idx];
            const gg = data[idx + 1];
            const bb = data[idx + 2];
            const luma = 0.2126 * rr + 0.7152 * gg + 0.0722 * bb;
            if (luma > maxL) maxL = luma;
          }
        }

        const thresholdFactor = isLinearLike ? 0.88 : isStrictEmitter ? 0.84 : 0.8;
        const dynamicThreshold = Math.max(isLinearLike ? 24 : isStrictEmitter ? 26 : 20, maxL * thresholdFactor);
        let sumW = 0;
        let sumX = 0;
        let sumY = 0;

        for (let yy = Math.max(0, sy - sampleRadius); yy <= Math.min(sampleH - 1, sy + sampleRadius); yy++) {
          for (let xx = Math.max(0, sx - sampleRadius); xx <= Math.min(sampleW - 1, sx + sampleRadius); xx++) {
            const idx = (yy * sampleW + xx) * 4;
            const rr = data[idx];
            const gg = data[idx + 1];
            const bb = data[idx + 2];
            const luma = 0.2126 * rr + 0.7152 * gg + 0.0722 * bb;
            if (luma < dynamicThreshold) continue;

            const dx = xx - sx;
            const dy = yy - sy;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > sampleRadius) continue;

            const centerBias = 1 / (1 + dist * (isLinearLike ? 0.38 : isStrictEmitter ? 0.42 : 0.28));
            const brightPower = isLinearLike ? 1.45 : isStrictEmitter ? 1.7 : 1.3;
            const w = Math.pow(luma - dynamicThreshold + 1, brightPower) * centerBias;
            sumW += w;
            sumX += xx * w;
            sumY += yy * w;
          }
        }

        if (sumW <= 1e-6) return { x: ox, y: oy };

        const refinedX = sumX / sumW / scale;
        const refinedY = sumY / sumW / scale;

        // 限制最大位移，避免在复杂纹理里被远处高亮“拖走”
        const maxShiftPx = isLinearLike ? 10 : 28;
        const dx = refinedX - ox;
        const dy = refinedY - oy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const shiftScale = dist > maxShiftPx && dist > 1e-6 ? maxShiftPx / dist : 1;
        let finalX = ox + dx * shiftScale;
        let finalY = oy + dy * shiftScale;
        // 暗光带优先贴近模型几何中心线，防止被外圈光晕吸附
        if (isLinearLike) {
          const pullBack = 0.4;
          finalX = ox + (finalX - ox) * (1 - pullBack);
          finalY = oy + (finalY - oy) * (1 - pullBack);

          // 二次约束：估计局部“槽线切线方向”，只允许沿切线较多移动，法线方向强抑制
          const dirRadius = Math.max(5, Math.round(sampleRadius * 0.9));
          let jxx = 0;
          let jxy = 0;
          let jyy = 0;
          for (let yy = Math.max(1, sy - dirRadius); yy <= Math.min(sampleH - 2, sy + dirRadius); yy++) {
            for (let xx = Math.max(1, sx - dirRadius); xx <= Math.min(sampleW - 2, sx + dirRadius); xx++) {
              const ddx = xx - sx;
              const ddy = yy - sy;
              const d2 = ddx * ddx + ddy * ddy;
              if (d2 > dirRadius * dirRadius) continue;

              const idxL = (yy * sampleW + (xx - 1)) * 4;
              const idxR = (yy * sampleW + (xx + 1)) * 4;
              const idxU = ((yy - 1) * sampleW + xx) * 4;
              const idxD = ((yy + 1) * sampleW + xx) * 4;
              const idxC = (yy * sampleW + xx) * 4;
              const lum = (ii: number) => 0.2126 * data[ii] + 0.7152 * data[ii + 1] + 0.0722 * data[ii + 2];
              const gx = (lum(idxR) - lum(idxL)) * 0.5;
              const gy = (lum(idxD) - lum(idxU)) * 0.5;
              const gm = Math.sqrt(gx * gx + gy * gy);
              if (gm < 2) continue;

              const lumaC = lum(idxC);
              const spatial = 1 / (1 + Math.sqrt(d2) * 0.22);
              const brightBias = 0.35 + Math.min(1, lumaC / 255);
              const ww = gm * spatial * brightBias;
              jxx += ww * gx * gx;
              jxy += ww * gx * gy;
              jyy += ww * gy * gy;
            }
          }

          const trace = jxx + jyy;
          if (trace > 1e-3) {
            // 最大特征向量≈法线方向；切线方向取其垂直向量
            const theta = 0.5 * Math.atan2(2 * jxy, jxx - jyy);
            const nx = Math.cos(theta);
            const ny = Math.sin(theta);
            const tx = -ny;
            const ty = nx;

            const ddx = finalX - ox;
            const ddy = finalY - oy;
            const along = ddx * tx + ddy * ty;
            const normal = ddx * nx + ddy * ny;

            const maxAlong = 6;   // 允许沿槽线小范围贴线移动（再收紧）
            const maxNormal = 1;  // 更强限制法线方向，防止被光晕拖拽
            const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
            const alongClamped = clamp(along, -maxAlong, maxAlong);
            const normalDamped = clamp(normal * 0.22, -maxNormal, maxNormal);

            finalX = ox + alongClamped * tx + normalDamped * nx;
            finalY = oy + alongClamped * ty + normalDamped * ny;
          }
        }
        return {
          x: Math.max(0, Math.min(naturalW, finalX)),
          y: Math.max(0, Math.min(naturalH, finalY)),
        };
      });

      return refined;
    } catch (e) {
      console.warn('[坐标微调] 亮度质心微调失败，回退原坐标:', e);
      return fixtures.map((f) => ({ x: Number(f.x) || 0, y: Number(f.y) || 0 }));
    }
  };

  const kelvinToColorTempParam = (kelvin: number): number => {
    const k = Math.max(2000, Math.min(10000, Math.round(kelvin || 6500)));
    if (k === 6500) return 0;
    if (k < 6500) return Math.round((k - 6500) / 90);
    return Math.round((k - 6500) / 170);
  };

  const estimateFixtureKelvinFromImage = async (
    sourceImageDataUrl: string,
    fixtures: Array<{ x: number; y: number; type?: string }>
  ): Promise<number[]> => {
    if (!sourceImageDataUrl || !Array.isArray(fixtures) || fixtures.length === 0) {
      return fixtures.map(() => 6500);
    }
    try {
      const img = new Image();
      img.src = sourceImageDataUrl;
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => reject(new Error('Kelvin estimate image load timeout')), 6000);
        img.onload = () => {
          clearTimeout(timeoutId);
          resolve();
        };
        img.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error('Kelvin estimate image load failed'));
        };
      });

      const naturalW = Math.max(1, img.naturalWidth || 1);
      const naturalH = Math.max(1, img.naturalHeight || 1);
      const targetMax = 1200;
      const scale = Math.min(1, targetMax / Math.max(naturalW, naturalH));
      const sampleW = Math.max(1, Math.round(naturalW * scale));
      const sampleH = Math.max(1, Math.round(naturalH * scale));

      const canvas = document.createElement('canvas');
      canvas.width = sampleW;
      canvas.height = sampleH;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return fixtures.map(() => 6500);
      ctx.drawImage(img, 0, 0, sampleW, sampleH);
      const { data } = ctx.getImageData(0, 0, sampleW, sampleH);
      const lumaAt = (idx: number) => 0.2126 * data[idx] + 0.7152 * data[idx + 1] + 0.0722 * data[idx + 2];

      const srgbToLinear = (v: number) => {
        const x = Math.max(0, Math.min(1, v / 255));
        return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
      };
      const rgbToKelvin = (r: number, g: number, b: number): number => {
        const rl = srgbToLinear(r);
        const gl = srgbToLinear(g);
        const bl = srgbToLinear(b);
        const X = 0.4124564 * rl + 0.3575761 * gl + 0.1804375 * bl;
        const Y = 0.2126729 * rl + 0.7151522 * gl + 0.0721750 * bl;
        const Z = 0.0193339 * rl + 0.1191920 * gl + 0.9503041 * bl;
        const sum = X + Y + Z;
        if (sum <= 1e-9) return 6500;
        const x = X / sum;
        const y = Y / sum;
        const denom = 0.1858 - y;
        if (Math.abs(denom) < 1e-6) return 6500;
        const n = (x - 0.3320) / denom;
        const cct = 449 * n * n * n + 3525 * n * n + 6823.3 * n + 5520.33;
        return Math.max(2000, Math.min(10000, Math.round(Number.isFinite(cct) ? cct : 6500)));
      };

      return fixtures.map((f) => {
        const t = String(f.type || '').toLowerCase();
        const sx = Math.max(0, Math.min(sampleW - 1, Math.round((Number(f.x) || 0) * scale)));
        const sy = Math.max(0, Math.min(sampleH - 1, Math.round((Number(f.y) || 0) * scale)));
        const radius = (() => {
          if (t === 'ambient_light' || t === 'natural_light' || t === 'window' || t === 'window_light' || t === 'sunlight') return 28;
          if (t === 'cove_light' || t === 'cove_lighting') return 16;
          return 12;
        })();

        let maxL = 0;
        for (let yy = Math.max(0, sy - radius); yy <= Math.min(sampleH - 1, sy + radius); yy++) {
          for (let xx = Math.max(0, sx - radius); xx <= Math.min(sampleW - 1, sx + radius); xx++) {
            const dx = xx - sx;
            const dy = yy - sy;
            if (dx * dx + dy * dy > radius * radius) continue;
            const i = (yy * sampleW + xx) * 4;
            maxL = Math.max(maxL, lumaAt(i));
          }
        }

        const threshold = Math.max(12, maxL * 0.72);
        let sumW = 0;
        let sumR = 0;
        let sumG = 0;
        let sumB = 0;
        for (let yy = Math.max(0, sy - radius); yy <= Math.min(sampleH - 1, sy + radius); yy++) {
          for (let xx = Math.max(0, sx - radius); xx <= Math.min(sampleW - 1, sx + radius); xx++) {
            const dx = xx - sx;
            const dy = yy - sy;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > radius) continue;
            const i = (yy * sampleW + xx) * 4;
            const luma = lumaAt(i);
            if (luma < threshold) continue;
            const centerBias = 1 / (1 + dist * 0.25);
            const w = Math.pow(luma - threshold + 1, 1.25) * centerBias;
            sumW += w;
            sumR += data[i] * w;
            sumG += data[i + 1] * w;
            sumB += data[i + 2] * w;
          }
        }

        if (sumW <= 1e-6) {
          if (t === 'natural_light' || t === 'window' || t === 'window_light' || t === 'sunlight') return 6200;
          if (t === 'ambient_light') return 5600;
          return 3500;
        }
        return rgbToKelvin(sumR / sumW, sumG / sumW, sumB / sumW);
      });
    } catch (e) {
      console.warn('[色温估算] 识别色温估算失败，回退默认色温:', e);
      return fixtures.map(() => 6500);
    }
  };

  const stabilizeLinearFixtureGroup = (
    fixtures: Array<{ type?: string }>,
    mappedAnchors: Array<{ x: number; y: number }>,
    refinedAnchors: Array<{ x: number; y: number }>,
    bounds: { width: number; height: number }
  ): Array<{ x: number; y: number }> => {
    const out = refinedAnchors.map((p) => ({ x: Number(p?.x) || 0, y: Number(p?.y) || 0 }));
    if (!Array.isArray(fixtures) || fixtures.length === 0) return out;

    const coveIndexes = fixtures
      .map((f, i) => ({ i, t: String(f?.type || '').toLowerCase() }))
      .filter((v) => v.t === 'cove_light' || v.t === 'cove_lighting')
      .map((v) => v.i);
    if (coveIndexes.length < 2) return out;

    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
    const info = coveIndexes.map((idx) => {
      const b = mappedAnchors[idx] || { x: 0, y: 0 };
      const r = refinedAnchors[idx] || b;
      const neighbors = coveIndexes
        .filter((j) => j !== idx)
        .map((j) => {
          const p = mappedAnchors[j] || { x: b.x, y: b.y };
          const dx = p.x - b.x;
          const dy = p.y - b.y;
          return { j, d2: dx * dx + dy * dy, dx, dy };
        })
        .sort((a, b2) => a.d2 - b2.d2);

      let tx = 1;
      let ty = 0;
      if (neighbors.length > 0) {
        const n = neighbors[0];
        const len = Math.sqrt(Math.max(1e-9, n.dx * n.dx + n.dy * n.dy));
        tx = n.dx / len;
        ty = n.dy / len;
      }
      if (tx < 0 || (Math.abs(tx) < 1e-6 && ty < 0)) {
        tx = -tx;
        ty = -ty;
      }
      const nx = -ty;
      const ny = tx;
      const dx = (Number(r.x) || 0) - (Number(b.x) || 0);
      const dy = (Number(r.y) || 0) - (Number(b.y) || 0);
      const along = dx * tx + dy * ty;
      const normal = dx * nx + dy * ny;
      // 曲率估计：两近邻方向越不共线（|dot|越小），曲率越高
      let curvature = 0;
      if (neighbors.length >= 2) {
        const n1 = neighbors[0];
        const n2 = neighbors[1];
        const l1 = Math.sqrt(Math.max(1e-9, n1.dx * n1.dx + n1.dy * n1.dy));
        const l2 = Math.sqrt(Math.max(1e-9, n2.dx * n2.dx + n2.dy * n2.dy));
        const u1x = n1.dx / l1;
        const u1y = n1.dy / l1;
        const u2x = n2.dx / l2;
        const u2y = n2.dy / l2;
        const dot = Math.max(-1, Math.min(1, u1x * u2x + u1y * u2y));
        curvature = Math.max(0, Math.min(1, 1 - Math.abs(dot)));
      }
      return { idx, b, tx, ty, nx, ny, along, normal, curvature };
    });

    const normalAbsSorted = info.map((v) => Math.abs(v.normal)).sort((a, b) => a - b);
    const medianAbsNormal = normalAbsSorted[Math.floor(normalAbsSorted.length / 2)] || 0;
    const groupSignRaw = info.reduce((acc, v) => acc + Math.sign(v.normal), 0);
    const groupSign = groupSignRaw === 0 ? 0 : Math.sign(groupSignRaw);

    for (const v of info) {
      // 动态半径锁：弯道更紧、直线稍松
      const curveT = clamp(v.curvature, 0, 1);
      const lockRadius = 4.2 - 2.2 * curveT; // 曲率高时约 2.0px，直线约 4.2px
      const maxAlong = 4.6 - 1.8 * curveT;
      const maxNormal = 0.6 - 0.35 * curveT;
      const alongClamped = clamp(v.along, -maxAlong, maxAlong);

      const normalGain = 0.16 - 0.08 * curveT;
      const normalBase = v.normal * normalGain;
      const outlierCap = medianAbsNormal > 1e-6 ? Math.max(0.45, medianAbsNormal * (1.2 - 0.2 * curveT)) : maxNormal;
      let normalLimited = clamp(normalBase, -Math.min(maxNormal, outlierCap), Math.min(maxNormal, outlierCap));
      if (groupSign !== 0 && normalLimited * groupSign < 0) {
        normalLimited *= 0.18;
      }

      const fx = v.b.x + alongClamped * v.tx + normalLimited * v.nx;
      const fy = v.b.y + alongClamped * v.ty + normalLimited * v.ny;
      // 最终保险：暗光带点位必须贴近模型几何中心线，限制离基准点半径
      const ldx = fx - v.b.x;
      const ldy = fy - v.b.y;
      const ldist = Math.sqrt(ldx * ldx + ldy * ldy);
      const lk = ldist > lockRadius && ldist > 1e-6 ? lockRadius / ldist : 1;
      const fxLocked = v.b.x + ldx * lk;
      const fyLocked = v.b.y + ldy * lk;
      out[v.idx] = {
        x: clamp(fxLocked, 0, Math.max(1, bounds.width)),
        y: clamp(fyLocked, 0, Math.max(1, bounds.height)),
      };
    }

    return out;
  };

  /**
   * 🚀 图片压缩工具函数：用于识别前压缩图片
   * 目标：只要不超过2048x2048，就可以压缩进行分析渲染
   * 压缩策略：如果图片超过2048px，压缩到2048px以内，质量 0.7
   */
  const compressImageForAnalysis = async (originalBase64: string): Promise<{ compressedBase64: string; scaleRatio: number; originalSize: number; compressedSize: number; compressedWidth: number; compressedHeight: number; originalWidth: number; originalHeight: number; coordinateMeta: AnalysisCoordinateMeta }> => {
    return new Promise(async (resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        const originalWidth = img.naturalWidth;
        const originalHeight = img.naturalHeight;
        
        // 计算原始大小（Base64 字符串长度）
        const originalSize = originalBase64.length;
        const originalSizeKB = (originalSize * 3 / 4 / 1024).toFixed(2); // Base64 转二进制大小估算
        
        console.log(`[图片压缩] 原始尺寸: ${originalWidth}x${originalHeight}, 原始大小: ${originalSizeKB} KB`);
        
        // 固定方图 contain + padding：模型输入统一为 1024x1024
        const targetSize = 1024;
        const scale = Math.min(targetSize / Math.max(1, originalWidth), targetSize / Math.max(1, originalHeight));
        const scaledWidth = Math.max(1, originalWidth * scale);
        const scaledHeight = Math.max(1, originalHeight * scale);
        const offsetX = (targetSize - scaledWidth) / 2;
        const offsetY = (targetSize - scaledHeight) / 2;
        const scaleRatio = 1 / Math.max(1e-6, scale); // 兼容旧日志字段（逆比例）

        console.log(`[图片压缩] 固定方图模式: base64=${targetSize}x${targetSize}, 内容=${scaledWidth.toFixed(3)}x${scaledHeight.toFixed(3)}, offset=(${offsetX.toFixed(3)}, ${offsetY.toFixed(3)}), scale=${scale.toFixed(8)}`);
        
        // 🔥 在创建新 Canvas 前，彻底清理所有旧的 WebGL 上下文（防止冲突）
        const existingCanvases = document.querySelectorAll('canvas');
        existingCanvases.forEach(existingCanvas => {
          try {
            // 只清理 WebGL 上下文，不清理 2D Canvas
            const existingGl = (existingCanvas as HTMLCanvasElement).getContext('webgl') || 
                              (existingCanvas as HTMLCanvasElement).getContext('webgl2');
            if (existingGl && existingGl instanceof WebGLRenderingContext) {
              const ext = existingGl.getExtension('WEBGL_lose_context');
              if (ext) {
                console.log('[图片压缩] 🧹 释放 WebGL 上下文，防止 Canvas 冲突');
                ext.loseContext();
              }
              // 尝试使用 forceContextLoss（如果可用）
              if (typeof (existingGl as any).forceContextLoss === 'function') {
                (existingGl as any).forceContextLoss();
              }
            }
          } catch (e) {
            // 忽略清理错误
          }
        });
        
        // 🔥 额外等待 800ms，确保 WebGL 上下文完全释放（给显存腾出物理空档）
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // 🔥 创建独立的、全新的离屏 Canvas 对象（临时，不添加到 DOM）
        // ⚠️ 绝对禁止使用 document.getElementById 获取页面上的 Canvas
        const canvas = document.createElement('canvas');
        canvas.width = targetSize;
        canvas.height = targetSize;
        
        // 🔥 确保这是 2D Canvas，不是 WebGL Canvas
        // 🔥 关键修复：使用 willReadFrequently: true 强制使用 CPU 辅助渲染，避开可能"卡死"的 GPU 路径
        const ctx = canvas.getContext('2d', {
          willReadFrequently: true, // 🔥 修复：告诉浏览器我们要频繁读取，强制使用 CPU 辅助，避开 WebGL 冲突的 GPU 路径
          alpha: false // 不需要透明度，节省内存
        });
        
        if (!ctx) {
          // 🔥 清理：立即释放 Canvas 内存
          canvas.width = 0;
          canvas.height = 0;
          reject(new Error('无法创建 Canvas 上下文'));
          return;
        }
        
        try {
          // 🔥 关键诊断：验证图像内容是否真的有效（检查是否为空图或全黑图）
          // 背景填充黑边，再将原图居中 contain 到 1024 方图
          ctx.fillStyle = '#000000';
          ctx.fillRect(0, 0, targetSize, targetSize);
          ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

          // 只在“真实内容区域”采样，避免抽到黑边导致误判
          const contentLeft = Math.max(0, Math.floor(offsetX));
          const contentTop = Math.max(0, Math.floor(offsetY));
          const contentWidth = Math.max(1, Math.floor(scaledWidth));
          const contentHeight = Math.max(1, Math.floor(scaledHeight));
          const sampleW = Math.max(1, Math.min(24, contentWidth));
          const sampleH = Math.max(1, Math.min(24, contentHeight));
          const sampleX = Math.max(
            0,
            Math.min(targetSize - sampleW, contentLeft + Math.floor((contentWidth - sampleW) / 2))
          );
          const sampleY = Math.max(
            0,
            Math.min(targetSize - sampleH, contentTop + Math.floor((contentHeight - sampleH) / 2))
          );

          const imageData = ctx.getImageData(sampleX, sampleY, sampleW, sampleH);
          const pixels = imageData.data;
          let hasValidPixels = false;
          let totalBrightness = 0;

          for (let i = 0; i < pixels.length; i += 4) {
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            const brightness = (r + g + b) / 3;
            totalBrightness += brightness;
            if (brightness > 3) hasValidPixels = true;
          }

          const avgBrightness = totalBrightness / (pixels.length / 4);
          console.log(
            `[图片压缩] 🔍 图像内容验证: 采样区=(${sampleX},${sampleY},${sampleW}x${sampleH}), 平均亮度=${avgBrightness.toFixed(1)}, 有有效像素=${hasValidPixels}`
          );

          // 极暗图像可能本来就是黑色画面，不应阻断流程；这里只记录警告
          if (!hasValidPixels && avgBrightness < 1) {
            console.warn('[图片压缩] ⚠️ 采样区域亮度极低，继续流程（不再因黑边/暗图误判中断）');
          }
          
          // 导出为 JPEG，质量 0.7
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          const compressedSize = compressedBase64.length;
          const compressedSizeKB = (compressedSize * 3 / 4 / 1024).toFixed(2);
          
          // 🔥 防空检查：验证压缩后的数据是否有效
          // 如果长度异常（小于 5000 字节），说明 Canvas 绘图可能为空，应立即终止
          const minValidSize = 5000; // 最小有效大小（字节）- 提高到 5000 确保图片真实有内容
          if (compressedSize < minValidSize) {
            console.error(`[图片压缩] ❌ 压缩后数据异常：长度 ${compressedSize} 字节，小于最小有效大小 ${minValidSize} 字节`);
            console.error(`[图片压缩] ❌ 可能原因：Canvas 绘图失败或 WebGL 上下文冲突导致导出空图`);
            // 🔥 清理：立即释放 Canvas 内存
            canvas.width = 0;
            canvas.height = 0;
            reject(new Error(`压缩后的图片数据异常（长度 ${compressedSize} 字节），可能是 Canvas 绘图失败。请刷新页面后重试。`));
            return;
          }
          
          // 🔥 调试选项：如果想查看实际生成的图片，可以取消下面的注释
          // const debugWindow = window.open();
          // if (debugWindow) {
          //   debugWindow.document.write(`<img src="${compressedBase64}" style="max-width: 100%;" />`);
          //   console.log('[图片压缩] 🔍 调试：已在新窗口打开压缩后的图片');
          // }
          
          console.log(`[图片压缩] ✅ 压缩完成`);
          console.log(`[图片压缩] 原始大小: ${originalSizeKB} KB`);
          console.log(`[图片压缩] 压缩后大小: ${compressedSizeKB} KB`);
          console.log(`[图片压缩] 压缩率: ${((1 - compressedSize / originalSize) * 100).toFixed(1)}%`);
          console.log(`[图片压缩] ✅ 数据验证通过：压缩后数据长度 ${compressedSize} 字节（≥ ${minValidSize} 字节）`);
          
          const coordinateMeta = buildAnalysisCoordinateMeta(
            originalWidth,
            originalHeight,
            targetSize,
            targetSize,
            offsetX,
            offsetY
          );
          console.log('[图片压缩] 坐标映射参数:', coordinateMeta);

          resolve({
            compressedBase64,
            scaleRatio,
            originalSize: originalSize,
            compressedSize: compressedSize,
            compressedWidth: targetSize, // 🔥 发送给模型的 Base64 实际宽度
            compressedHeight: targetSize, // 🔥 发送给模型的 Base64 实际高度
            originalWidth: originalWidth, // 🔥 保存原图尺寸
            originalHeight: originalHeight,
            coordinateMeta,
          });
        } catch (drawError) {
          console.error(`[图片压缩] ❌ Canvas 绘图失败:`, drawError);
          // 🔥 清理：发生错误时也要释放 Canvas 内存
          canvas.width = 0;
          canvas.height = 0;
          reject(new Error(`Canvas 绘图失败: ${drawError instanceof Error ? drawError.message : '未知错误'}`));
          return;
        } finally {
          // 🔥 关键：函数执行完毕后立即释放 Canvas 内存
          // 设置 width 和 height 为 0 可以立即释放 Canvas 占用的内存
          try {
            canvas.width = 0;
            canvas.height = 0;
            console.log(`[图片压缩] 🧹 Canvas 内存已释放`);
          } catch (cleanupError) {
            // 忽略清理错误
          }
        }
      };
      
      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };
      
      img.src = originalBase64;
    });
  };

  /** 灯光识别：仅用户点击「识别」后执行，调用后端 /analyze（老张 API 优先） */
  const handleRecognizeFixtures = async () => {
    if (isAnalyzing || isRendering || project.isScanning || project.isProcessing) {
      alert(language !== 'zh'
        ? 'Image analysis or rendering in progress, please wait...'
        : '图片正在分析或渲染中，请稍候...');
      return;
    }
    const base64 = project.originalImage;
    if (!base64) {
      alert(language !== 'zh'
        ? 'Please load an image first, then tap Recognize.'
        : '请先加载图片，再点击「识别」。');
      return;
    }

    const ADMIN_EMAIL = '276517619@qq.com';
    const isAdminUser = user?.email === ADMIN_EMAIL;
    /** UI 与后端一致：每图 2 次识别入口（含管理员账号；后端仍可对管理员绕过扣次，但侧栏必须锁死防误触） */
    if (lightingIdentifyCountRef.current >= 2) {
      alert(
        language !== 'zh'
          ? 'You have used 2 recognition runs on this image. Tap Generate to render (charged), then you can recognize again. Or load another image.'
          : '同一张图已用完 2 次「识别光源」（第 1 次免费，第 2 次为最后一次）。请先点击「生成」出图（将扣费），生成成功后才能再识别；或更换/重新载入图片。',
      );
      return;
    }

    if (recognizeFixtureBusyRef.current) {
      alert(
        language !== 'zh'
          ? 'Recognition is already running. Please wait.'
          : '识别正在进行中，请稍候…',
      );
      return;
    }
    recognizeFixtureBusyRef.current = true;

    setIsAnalyzing(true);
    setProject(prev => ({ ...prev, isScanning: true }));

    try {
      console.log('🔄 开始识别图片中的光源（每任务最多 2 次；taskId 将随请求提交）...');

      console.log('[识别流程] 步骤1: 压缩图片用于识别...');
      const { compressedBase64, scaleRatio, compressedWidth, compressedHeight, originalWidth, originalHeight, coordinateMeta } =
        await compressImageForAnalysis(base64);

      setCompressedImageDimensions({ width: compressedWidth, height: compressedHeight });
      setOriginalImageDimensions({ width: originalWidth, height: originalHeight });
      console.log(`[识别流程] 压缩图尺寸已保存: ${compressedWidth}x${compressedHeight}`);
      console.log(`[识别流程] 原图尺寸已保存: ${originalWidth}x${originalHeight}（用于渲染时确保输出尺寸一致）`);

      console.log('[识别流程] 步骤2: 使用压缩后的图片发送到后端识别...');

      const compressedDataLength = compressedBase64.length;
      const minValidSize = 5000;
      if (compressedDataLength < minValidSize) {
        console.error(`[识别流程] ❌ 压缩后数据异常：长度 ${compressedDataLength} 字节，小于最小有效大小 ${minValidSize} 字节`);
        setProject(prev => ({ ...prev, isScanning: false }));
        setIsAnalyzing(false);
        alert(language !== 'zh'
          ? 'Image compression failed. Please refresh the page and try again.'
          : '图片压缩失败。请刷新页面后重试。');
        return;
      }
      console.log(`[识别流程] ✅ 数据验证通过：压缩后数据长度 ${compressedDataLength} 字节（≥ ${minValidSize} 字节）`);

      let foundFixtures: any[] = [];
      let spatialHints: SpatialFixtureHint[] = [];

      /** 默认不弹「识别·并行请求结果」大窗。成功仅 console；硬失败 alert；调试：localStorage nemo_show_recognition_report=1 */
      const commitRecognitionSyncReport = (
        r: RecognitionSyncReport,
        opts?: { suppressHardFailAlert?: boolean },
      ) => {
        setRecognitionSyncReport(r);
        let debug = false;
        try {
          debug = localStorage.getItem('nemo_show_recognition_report') === '1';
        } catch {
          /* ignore */
        }
        /** 仅灯具识别失败为硬失败；深度/法线缺失时仍可使用识别结果（无空间贴图），避免 RunningHub 波动导致整次识别作废 */
        const hardFail = !r.geminiOk;
        const depthDegraded = r.geminiOk && !r.depthNormalOk;
        const spatialOnlyFail = r.geminiOk && r.depthNormalOk && r.spatialMappingOk === false;

        if (debug) {
          setShowRecognitionSyncReport(true);
          return;
        }

        setShowRecognitionSyncReport(false);

        if (hardFail) {
          console.warn('[识别][并行] 失败', r);
          if (!opts?.suppressHardFailAlert) {
            alert(
              language !== 'zh'
                ? 'Recognition failed. Please recognize again.'
                : '识别失败，请重新识别。',
            );
          }
          return;
        }

        if (depthDegraded) {
          console.warn('[识别][并行] 深度/法线未就绪（已跳过空间增强，灯具识别仍可用）', r.depthError);
        }
        if (spatialOnlyFail) {
          console.warn('[识别][并行] 空间映射未完全成功（已回退默认）', r.spatialMappingError);
        } else if (!depthDegraded) {
          console.log('[识别][并行] 全部成功', {
            fixtures: r.geminiFixtureCount,
            time: new Date(r.at).toISOString(),
          });
        }
      };

      try {
        let analysisResult: { fixtures?: any[] } = { fixtures: [] };
        let depthNormalRaw: { depthImage?: string; normalImage?: string } | null = null;
        let spatialMappingOk: boolean | undefined;
        let spatialMappingError: string | undefined;

        let taskIdForAnalyze: string | null | undefined = isAdminUser
          ? undefined
          : lightingTaskIdRef.current || lightingTaskId;
        if (!isAdminUser && user?.email) {
          if (!taskIdForAnalyze) {
            /**
             * 禁止在「已用过识别」但 taskId 丢失时再新建任务：否则会拿到全新 2 次额度，等于无限刷识别 API。
             * 只能让用户重新上传或载入历史以绑定新 task。
             */
            if (lightingIdentifyCountRef.current > 0 || recognitionCompletedForCurrentImage) {
              setProject((prev) => ({ ...prev, isScanning: false }));
              setIsAnalyzing(false);
              setRecognitionScanPhase(null);
              alert(
                language !== 'zh'
                  ? 'Recognition session lost (no task id). Please re-upload the image or load it from history to continue.'
                  : '识别任务状态已丢失（无任务 ID），无法继续识别。请重新上传图片或从历史重新载入。',
              );
              return;
            }
            taskIdForAnalyze = await createLightingTaskForCurrentUser();
          }
          if (!taskIdForAnalyze) {
            setProject((prev) => ({ ...prev, isScanning: false }));
            setIsAnalyzing(false);
            setRecognitionScanPhase(null);
            alert(
              language !== 'zh'
                ? 'Could not create a recognition task. Please try again.'
                : '无法创建识别任务，请稍后重试。',
            );
            return;
          }
        }

        if (lightingIdentifyCountRef.current >= 2) {
          setProject((prev) => ({ ...prev, isScanning: false }));
          setIsAnalyzing(false);
          setRecognitionScanPhase(null);
          alert(
            language !== 'zh'
              ? 'Recognition limit reached. Tap Generate first.'
              : '已达到本图识别次数上限，请先点击「生成」。',
          );
          return;
        }
        {
          const next = lightingIdentifyCountRef.current + 1;
          lightingIdentifyCountRef.current = next;
          flushSync(() => {
            setLightingIdentifyCount(next);
          });
        }

        console.log('[App.tsx] Promise.allSettled：Gemini + RunningHub 空间结构数据，Base64长度:', compressedBase64.length);
        setRecognitionScanPhase('spatial');
        const settled = await Promise.allSettled([
          analyzeImageForFixtures(compressedBase64, taskIdForAnalyze ?? null),
          api.submitDepthNormalRunningHub(
            compressedBase64,
            { width: compressedWidth, height: compressedHeight },
            user?.email ?? undefined,
          ),
        ]);
        setRecognitionScanPhase('full');

        const g = settled[0];
        const d = settled[1];
        if (g.status === 'rejected') {
          const reason = (g as PromiseRejectedResult).reason;
          // Step 1 ApiError 兼容：后端 body.code 现在挂在 reason.serverCode；
          // 旧路径（非 ApiError）继续读 reason.code。
          const r =
            reason && typeof reason === 'object'
              ? (reason as { code?: string; serverCode?: string })
              : undefined;
          const backendCode = r?.serverCode ?? r?.code;
          if (backendCode === 'IDENTIFY_LIMIT') {
            setLightingIdentifyCount(2);
            lightingIdentifyCountRef.current = 2;
          }
        }
        const geminiOk = g.status === 'fulfilled';
        const depthVal = d.status === 'fulfilled' ? (d.value as Record<string, unknown>) : null;
        const depthOk = !!(depthVal && depthVal.depthImage && depthVal.normalImage);

        analysisResult = geminiOk ? (g as PromiseFulfilledResult<{ fixtures?: any[]; identifyCount?: number }>).value : { fixtures: [] };
        if (!isAdminUser && geminiOk) {
          const ic = (analysisResult as { identifyCount?: number }).identifyCount;
          if (typeof ic === 'number' && Number.isFinite(ic)) {
            const icN = ic;
            const srv = Math.min(2, Math.max(0, Math.round(icN)));
            const licCur = lightingIdentifyCountRef.current ?? 0;
            const n = Math.min(2, Math.max(licCur, srv));
            if (n !== lightingIdentifyCountRef.current) {
              lightingIdentifyCountRef.current = n;
              setLightingIdentifyCount(n);
            }
          }
        }
        depthNormalRaw = depthOk
          ? { depthImage: String(depthVal!.depthImage), normalImage: String(depthVal!.normalImage) }
          : null;

        if (depthNormalRaw?.depthImage && depthNormalRaw?.normalImage) {
          const fetchSpatial = (url: string) => api.fetchImageDataUrl(url);
          const [depthData, normalData] = await Promise.all([
            ensureSpatialMapDataUrlOrNull(depthNormalRaw.depthImage, fetchSpatial),
            ensureSpatialMapDataUrlOrNull(depthNormalRaw.normalImage, fetchSpatial),
          ]);
          if (depthData && normalData) {
            depthNormalRaw = { depthImage: depthData, normalImage: normalData };
          } else {
            console.warn('[识别] depth/normal 转 data URL 未全部成功，不写入 spatial 贴图');
            depthNormalRaw = null;
          }
        }

        const geminiErr =
          g.status === 'rejected'
            ? String((g as PromiseRejectedResult).reason?.message || (g as PromiseRejectedResult).reason)
            : undefined;
        const depthErr =
          d.status === 'rejected'
            ? String((d as PromiseRejectedResult).reason?.message || (d as PromiseRejectedResult).reason)
            : !depthOk && d.status === 'fulfilled'
              ? '未返回 depthImage / normalImage'
              : undefined;

        if (!geminiOk) {
          commitRecognitionSyncReport({
            at: Date.now(),
            geminiOk: false,
            geminiError: geminiErr,
            geminiFixtureCount: Array.isArray(analysisResult?.fixtures) ? analysisResult.fixtures!.length : 0,
            depthNormalOk: depthOk,
            depthError: depthErr,
            depthImageDataUrl: depthVal?.depthImage ? String(depthVal.depthImage) : undefined,
            normalImageDataUrl: depthVal?.normalImage ? String(depthVal.normalImage) : undefined,
          });
          throw new Error('SYNC_PARTIAL_FAIL');
        }

        if (!depthOk) {
          console.warn('[识别][并行] RunningHub 深度/法线请求未完成，继续灯具识别流程（无 depth/normal 贴图）', depthErr);
        }

        foundFixtures = Array.isArray(analysisResult?.fixtures) ? analysisResult.fixtures : [];

        if (
          depthNormalRaw &&
          depthNormalRaw.depthImage &&
          depthNormalRaw.normalImage &&
          foundFixtures.length > 0
        ) {
          const metaSpatial: SpatialCoordinateMeta = {
            base64Width: coordinateMeta.base64Width,
            base64Height: coordinateMeta.base64Height,
            offsetX: coordinateMeta.offsetX,
            offsetY: coordinateMeta.offsetY,
            scale: coordinateMeta.scale,
            originalWidth: coordinateMeta.originalWidth,
            originalHeight: coordinateMeta.originalHeight,
          };
          try {
            const spatialCtx = await createOffscreenSpatialMaps(
              depthNormalRaw.depthImage,
              depthNormalRaw.normalImage,
              originalWidth,
              originalHeight,
              metaSpatial,
            );
            spatialHints = processSpatialMapping(foundFixtures, spatialCtx);
            spatialMappingOk = true;
          } catch (spErr: any) {
            spatialMappingOk = false;
            spatialMappingError = spErr?.message || String(spErr);
            console.warn('[spatial] processSpatialMapping 失败，跳过后续空间增强', spErr);
            spatialHints = foundFixtures.map(() => ({ intensityScale: 1 }));
          }
          setProject((prev) => ({
            ...prev,
            depthImageDataUrl: depthNormalRaw.depthImage ?? null,
            normalImageDataUrl: depthNormalRaw.normalImage ?? null,
            spatialCoordinateMeta: metaSpatial,
          }));
        }

        commitRecognitionSyncReport({
          at: Date.now(),
          geminiOk: true,
          geminiFixtureCount: foundFixtures.length,
          depthNormalOk: !!depthNormalRaw?.depthImage,
          depthImageDataUrl: depthNormalRaw?.depthImage,
          normalImageDataUrl: depthNormalRaw?.normalImage,
          spatialMappingOk,
          spatialMappingError,
        });

        if (foundFixtures.length === 0) {
          setRecognitionCompletedForCurrentImage(true);
          setProject(prev => ({ ...prev, isScanning: false }));
          setIsAnalyzing(false);
          setRecognitionScanPhase(null);
          alert(language !== 'zh'
            ? 'No light sources detected. Please try another image or manually add light points.'
            : '未能识别到光源。请尝试其他图片或手动添加灯光点。');
          return;
        }
      } catch (analysisError: any) {
        console.error('❌ 空间数据同步 / 识别失败:', analysisError);
        foundFixtures = [];
        setProject(prev => ({ ...prev, isScanning: false }));
        setIsAnalyzing(false);
        setRecognitionScanPhase(null);
        const msg = analysisError?.message || '';
        const authErr =
          msg.includes('未授权') || msg.includes('Unauthorized') || msg.includes('401');
        if (msg !== 'SYNC_PARTIAL_FAIL') {
          commitRecognitionSyncReport(
            {
              at: Date.now(),
              geminiOk: false,
              geminiError: msg,
              geminiFixtureCount: 0,
              depthNormalOk: false,
            },
            { suppressHardFailAlert: authErr },
          );
        }
        if (authErr) {
          alert(language !== 'zh'
            ? 'Image analysis failed due to authorization error. You can manually add light sources using the "+ ADD LIGHT SOURCE" button.'
            : '图片分析失败（授权错误）。您可以使用 "+ ADD LIGHT SOURCE" 按钮手动添加光源。');
        }
        return;
      }

      if (foundFixtures.length === 0) {
        setRecognitionCompletedForCurrentImage(true);
        setProject(prev => ({ ...prev, isScanning: false }));
        setIsAnalyzing(false);
        alert(language !== 'zh'
          ? 'No light sources detected. Please try another image or manually add light points.'
          : '未能识别到光源。请尝试其他图片或手动添加灯光点。');
        return;
      }

      console.log('[识别流程] 步骤3: 将识别坐标映射回原图尺寸（用于前端显示）...');
      console.log(`[坐标映射] 缩放比例: ${scaleRatio.toFixed(2)}, 压缩图尺寸: ${compressedWidth}x${compressedHeight}`);

      const mappedFixtures = foundFixtures.map((f) => {
        const mapped = mapModelNormalizedToOriginalPixel(f.x, f.y, coordinateMeta);
        return {
          ...f,
          x: mapped.x,
          y: mapped.y,
        };
      });

      const refinedAnchorsRaw = await refineFixtureAnchorsByLuminance(base64, mappedFixtures);
      const refinedAnchors = stabilizeLinearFixtureGroup(
        foundFixtures,
        mappedFixtures,
        refinedAnchorsRaw,
        { width: originalWidth, height: originalHeight }
      );
      const estimatedKelvinList = await estimateFixtureKelvinFromImage(
        base64,
        refinedAnchors.map((p, idx) => ({ x: p.x, y: p.y, type: foundFixtures[idx]?.type }))
      );

      const fixturesWithDefaults = foundFixtures.map((f, i) => {
        const mapped = mappedFixtures[i];
        const refined = refinedAnchors[i] || { x: mapped.x, y: mapped.y };
        const detectedKelvin = Math.max(2000, Math.min(10000, Math.round(estimatedKelvinList[i] || 6500)));
        console.log(`[坐标映射] 光源 ${i + 1}: AI识别坐标 (${f.x}, ${f.y}) / 1000 -> 原图坐标 (${mapped.x}, ${mapped.y}) -> 质心微调 (${Math.round(refined.x)}, ${Math.round(refined.y)}) / 原图尺寸 (${originalWidth}x${originalHeight})`);
        const sc =
          f.type === 'cove_light' || f.type === 'cove_lighting'
            ? normalizeStripContext((f as any).strip_context ?? (f as any).stripContext)
            : undefined;
        const sa =
          f.type === 'cove_light' || f.type === 'cove_lighting'
            ? normalizeStripAxis((f as any).strip_axis ?? (f as any).stripAxis) ??
              defaultAxisForStripContext(sc ?? 'generic')
            : undefined;
        let dlc =
          f.type === 'downlight' || f.type === 'spotlight'
            ? normalizeDownlightContext((f as any).downlight_context ?? (f as any).downlightContext)
            : undefined;
        let bp =
          f.type === 'downlight' || f.type === 'spotlight'
            ? normalizeBeamProfile((f as any).beam_profile ?? (f as any).beamProfile) ??
              defaultBeamProfileForContext(dlc ?? 'generic')
            : undefined;
        const hint = spatialHints[i];
        if (hint && (f.type === 'downlight' || f.type === 'spotlight')) {
          if (hint.beamProfile) {
            const hb = normalizeBeamProfile(hint.beamProfile);
            if (hb) bp = hb;
          }
          if (hint.downlightContext) {
            const hd = normalizeDownlightContext(hint.downlightContext);
            if (hd) dlc = hd;
          }
        }
        const spatialScale = hint?.intensityScale ?? 1;
        const spatialNormal =
          hint?.nx !== undefined && hint?.ny !== undefined && hint?.nz !== undefined
            ? { nx: hint.nx, ny: hint.ny, nz: hint.nz }
            : undefined;
        const spatialZNorm = hint?.zNorm;
        const fixtureType = normalizeFixtureType(f.type);
        const fixtureLabel = normalizeFixtureLabel(f.label, fixtureType);
        return {
          ...f,
          type: fixtureType,
          label: fixtureLabel,
          x: Math.round(refined.x),
          y: Math.round(refined.y),
          _modelNormalizedX: f.x,
          _modelNormalizedY: f.y,
          _analysisCoordMeta: coordinateMeta,
          _anchorRefined: true,
          _originalCompressedX: f.x,
          _originalCompressedY: f.y,
          _compressedWidth: compressedWidth,
          _compressedHeight: compressedHeight,
          id: `fixture-${Date.now()}-${i}`,
          intensity: Math.max(0.15, Math.min(2, 1.0 * spatialScale)),
          spatialIntensityScale: spatialScale,
          ...(spatialNormal ? { spatialNormal, spatialZNorm } : {}),
          colorTemp: kelvinToColorTempParam(detectedKelvin),
          detectedKelvin,
          hue: 0,
          saturation: 100,
          lightness: 50,
          isModified: false,
          source: 'auto' as const,
          locked: true,
          ...(sc !== undefined ? { stripContext: sc, stripAxis: sa } : {}),
          ...((dlc !== undefined && bp) ? { downlightContext: dlc, beamProfile: bp } : {}),
          ...(f.type === 'spotlight' && {
            rotation: 90,
            beamAngle: 24,
          }),
        };
      });

      if (fixturesWithDefaults.length === 0) {
        setProject(prev => ({ ...prev, isScanning: false }));
        setIsAnalyzing(false);
        return;
      }

      replaceFixtureBaseline(fixturesWithDefaults);
      setRecognitionCompletedForCurrentImage(true);

      setProject(prev => ({
        ...prev,
        fixtures: fixturesWithDefaults,
        isScanning: false,
        selectedFixtureIds: fixturesWithDefaults.length > 0 ? [fixturesWithDefaults[0].id] : [],
      }));
      // 识别完成后自动切回“原图视图”，让用户第一时间看到识别光点
      setShowOriginal(true);

      setUndoHistory([{ fixtures: JSON.parse(JSON.stringify(fixturesWithDefaults)), selectedFixtureIds: fixturesWithDefaults.length > 0 ? [fixturesWithDefaults[0].id] : [] }]);
      setUndoHistoryIndex(0);
      setIsAnalyzing(false);
    } catch (err: any) {
      console.error('Fixture analysis error:', err);
      // Step 1 ApiError 兼容：后端 body.code 现在挂在 err.serverCode；
      // 旧路径（非 ApiError）继续读 err.code。
      const backendCode = err?.serverCode ?? err?.code;
      if (backendCode === 'IDENTIFY_LIMIT') {
        setLightingIdentifyCount(2);
        lightingIdentifyCountRef.current = 2;
      }
      setProject(prev => ({ ...prev, isScanning: false }));
      const errorMessage = err?.message || 'Unknown error';
      if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorMessage.includes('Failed to fetch')) {
        alert(language !== 'zh'
          ? 'Failed to connect to backend server. Please check if the backend service is running.'
          : '无法连接到后端服务器。请检查后端服务是否正在运行。');
      } else if (errorMessage.includes('GEMINI_API_KEY') || errorMessage.includes('未配置')) {
        alert(language !== 'zh'
          ? 'Gemini API key not configured. Please configure it in the backend .env file.'
          : 'Gemini API 密钥未配置。请在后端的 .env 文件中配置。');
      } else {
        alert(language !== 'zh'
          ? `Failed to analyze image: ${errorMessage}`
          : `分析图片失败: ${errorMessage}`);
      }
    } finally {
      recognizeFixtureBusyRef.current = false;
      setIsAnalyzing(false);
      setRecognitionScanPhase(null);
      setProject(prev => ({ ...prev, isScanning: false }));
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (isAnalyzing || isRendering || project.isScanning || project.isProcessing) {
        console.warn('⚠️ 正在分析或渲染中，请勿重复操作');
        e.target.value = '';
        alert(language !== 'zh'
          ? 'Image analysis or rendering in progress, please wait...'
          : '图片正在分析或渲染中，请稍候...');
        return;
      }

      /** 侧栏「加载图片」与画布「点击加载图片」共用此 input：禁止 GIF 动图 */
      if (isGifFile(file)) {
        e.target.value = '';
        alert(translateAppI18n('error_gif_not_supported', language));
        return;
      }

      e.target.value = '';

      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result as string;
        let resolutionCheckPassed = false;
        let imgNaturalWidth = 0;
        let imgNaturalHeight = 0;

        try {
          const img = new Image();
          img.src = base64;

          await new Promise<void>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              reject(new Error('Image load timeout'));
            }, 5000);

            img.onload = () => {
              clearTimeout(timeoutId);
              const width = img.naturalWidth;
              const height = img.naturalHeight;
              imgNaturalWidth = width;
              imgNaturalHeight = height;

              console.log(`[分辨率检查] 图片尺寸: ${width}x${height}`);

              if (width > 2048 || height > 2048) {
                const errorMsg = language !== 'zh'
                  ? `Image resolution exceeds 2048x2048. Current resolution: ${width}x${height}. Please use an image with resolution 2048x2048 or smaller.`
                  : `图片分辨率超过2048x2048。当前分辨率：${width}x${height}。请使用分辨率不超过2048x2048的图片。`;
                alert(errorMsg);
                reject(new Error('Image resolution too large'));
                return;
              }
              console.log('[分辨率检查] ✅ 图片尺寸符合要求');
              resolutionCheckPassed = true;
              resolve();
            };

            img.onerror = () => {
              clearTimeout(timeoutId);
              reject(new Error('Failed to load image'));
            };
          });
        } catch (err: any) {
          console.error('图片分辨率检查失败:', err);
          if (err.message === 'Image resolution too large') {
            e.target.value = '';
            return;
          }
          alert(
            language !== 'zh'
              ? 'Failed to check image resolution. Please try again with a valid image file.'
              : '无法检查图片分辨率。请使用有效的图片文件重试。'
          );
          e.target.value = '';
          return;
        }

        if (!resolutionCheckPassed) {
          e.target.value = '';
          return;
        }

        fixtureBaselineByIdRef.current = {};
        setProject({
          originalImage: base64,
          renderedImage: null,
          isScanning: false,
          fixtures: [],
          selectedFixtureIds: [],
          isProcessing: false,
          depthImageDataUrl: null,
          normalImageDataUrl: null,
          spatialCoordinateMeta: null,
        });
        /** 新主图必须先清零；若仅依赖 createLightingTask，请求失败时会沿用上一张图的 count=2，侧栏会误判「识别已关闭」 */
        setLightingIdentifyCount(0);
        lightingIdentifyCountRef.current = 0;

        setShowOriginal(false);
        setCompressedImageDimensions(null);
        setOriginalImageDimensions({ width: imgNaturalWidth, height: imgNaturalHeight });
        setUndoHistory([]);
        setUndoHistoryIndex(-1);

        if (user?.email) {
          await createLightingTaskForCurrentUser();
        } else {
          lightingTaskIdRef.current = null;
          setLightingTaskId(null);
        }
      };

      reader.onerror = () => {
        console.error('文件读取失败');
        alert(language !== 'zh'
          ? 'Failed to read image file. Please try again.'
          : '图片文件读取失败，请重试。');
      };

      reader.readAsDataURL(file);
    }
  };

  /** 范围选择锚点：上一次普通点击的灯 id；Ctrl+点击 为列表区间连选；Shift+点击 为追加多选（与侧栏说明一致） */
  const fixtureSelectionAnchorIdRef = useRef<string | null>(null);

  const handleSelectFixture = useCallback((id: string, opts?: FixtureSelectOptions) => {
    const additive = !!opts?.additive;
    const range = !!opts?.range;

    setProject((prev) => {
      const fixtures = prev.fixtures;
      const idx = fixtures.findIndex((f) => f.id === id);
      if (idx < 0) return prev;

      if (range) {
        const anchorId = fixtureSelectionAnchorIdRef.current;
        if (anchorId) {
          const anchorIdx = fixtures.findIndex((f) => f.id === anchorId);
          if (anchorIdx >= 0) {
            const lo = Math.min(anchorIdx, idx);
            const hi = Math.max(anchorIdx, idx);
            const rangeIds = fixtures.slice(lo, hi + 1).map((f) => f.id);
            return { ...prev, selectedFixtureIds: rangeIds };
          }
        }
      }

      if (additive) {
        let nextIds: string[];
        if (prev.selectedFixtureIds.includes(id)) {
          nextIds = prev.selectedFixtureIds.filter((fid) => fid !== id);
        } else {
          nextIds = [...prev.selectedFixtureIds, id];
        }
        fixtureSelectionAnchorIdRef.current = id;
        return { ...prev, selectedFixtureIds: nextIds.length ? nextIds : [id] };
      }

      fixtureSelectionAnchorIdRef.current = id;
      return { ...prev, selectedFixtureIds: [id] };
    });
  }, []);

  // 🔥 保存当前状态到撤销历史栈
  const saveToUndoHistory = useCallback((fixtures: Fixture[], selectedFixtureIds: string[]) => {
    setUndoHistory(prev => {
      // 如果当前不在历史栈末尾，删除后面的历史（分支操作）
      const newHistory = prev.slice(0, undoHistoryIndex + 1);
      // 添加新状态
      const updated = [...newHistory, { fixtures: JSON.parse(JSON.stringify(fixtures)), selectedFixtureIds: [...selectedFixtureIds] }];
      // 最多保存50步
      const limited = updated.slice(-50);
      setUndoHistoryIndex(limited.length - 1);
      return limited;
    });
  }, [undoHistoryIndex]);

  // 🔥 撤销操作（Ctrl+Z）
  const handleUndo = useCallback(() => {
    if (undoHistoryIndex > 0) {
      const prevState = undoHistory[undoHistoryIndex - 1];
      setProject(prev => ({
        ...prev,
        fixtures: JSON.parse(JSON.stringify(prevState.fixtures)),
        selectedFixtureIds: [...prevState.selectedFixtureIds],
        renderedImage: null // 撤销后清除渲染结果
      }));
      setUndoHistoryIndex(prev => prev - 1);
      console.log(`撤销到第 ${undoHistoryIndex - 1} 步`);
    } else if (undoHistoryIndex === 0) {
      // 撤销到初始状态
      const initialState = undoHistory[0];
      setProject(prev => ({
        ...prev,
        fixtures: JSON.parse(JSON.stringify(initialState.fixtures)),
        selectedFixtureIds: [...initialState.selectedFixtureIds],
        renderedImage: null
      }));
      setUndoHistoryIndex(-1);
      console.log('撤销到初始状态');
    }
  }, [undoHistory, undoHistoryIndex]);

  // 🔥 添加光源点（手动添加）
  const handleAddFixture = useCallback((fixture: Omit<Fixture, 'id'>) => {
    const isManual = (fixture as any).source === 'manual';
    const newFixture: Fixture = {
      ...fixture,
      id: `fixture-manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      // 规则：只有“被用户调整过”的灯才生效（isModified=true）。
      // 手动新增本身不应改变画面；移动/改参数会在其它逻辑里置 isModified=true。
      isModified: !!fixture.isModified,
      // 射灯/筒灯默认下照，避免手动新增时方向漂移
      ...((fixture.type === 'spotlight' || fixture.type === 'downlight') && {
        rotation: fixture.rotation ?? 90,  // 默认90°，垂直向下
        beamAngle: fixture.beamAngle ?? 24  // 默认24°，更接近真实筒灯/射灯下照
      })
    };

    // 🔥 关键修复：手动新增点位也必须进入 1024x1024 contain+padding 的同一坐标映射体系，
    // 否则 UI 像素坐标与后端/蒙版使用的 0-1000 坐标系不一致，会导致“光位跑偏到角落”。
    if (isManual) {
      const ow = originalImageDimensions?.width;
      const oh = originalImageDimensions?.height;
      if (ow && oh) {
        const meta = buildDefaultSquareCoordinateMeta(ow, oh);
        // 手动点击在 Editor 内部存的是“原图归一化(0-1000)”，先还原为原图像素，再映射到 1024 方图归一化
        const origNormX = Number(newFixture.x) || 0;
        const origNormY = Number(newFixture.y) || 0;
        const origPxX = (origNormX / 1000) * ow;
        const origPxY = (origNormY / 1000) * oh;
        const modelNorm = mapOriginalPixelToModelNormalized(origPxX, origPxY, meta);
        (newFixture as any)._analysisCoordMeta = meta;
        (newFixture as any)._modelNormalizedX = modelNorm.x;
        (newFixture as any)._modelNormalizedY = modelNorm.y;
      }
    }

    fixtureBaselineByIdRef.current[newFixture.id] = JSON.parse(JSON.stringify(newFixture)) as Fixture;

    setProject(prev => {
      // 保存到撤销历史
      saveToUndoHistory(prev.fixtures, prev.selectedFixtureIds);
      
      return {
        ...prev,
        fixtures: [...prev.fixtures, newFixture],
        selectedFixtureIds: [newFixture.id], // 自动选中新添加的点
        renderedImage: null // 新增光点后旧结果作废，避免后续调试/对比串扰
      };
    });
    
    console.log('✅ 手动添加光源点:', newFixture);
  }, [saveToUndoHistory]);

  // 🔥 移动光源点位置
  const handleMoveFixture = useCallback((id: string, x: number, y: number) => {
    setProject(prev => {
      // 🔥 只在第一次移动时保存到历史（避免频繁保存）
      const isFirstMove = !prev.fixtures.find(f => f.id === id)?.isModified;
      if (isFirstMove) {
        saveToUndoHistory(prev.fixtures, prev.selectedFixtureIds);
      }
      
      const updatedFixtures = prev.fixtures.map(f => {
        // 🔥 如果点已锁定，不允许移动
        const isLocked = (f as any).locked !== undefined ? (f as any).locked : ((f as any).source === 'auto' || (f as any).source === undefined);
        if (f.id === id && isLocked) {
          console.log('⚠️ 光源点已锁定，无法移动');
          return f;
        }
        if (f.id !== id) return f;
        const nextBase: Fixture = { ...f, x, y, isModified: true };
        // 手动点：移动后必须同步 1024 方图归一化字段，否则 Editor 仍按旧 _modelNormalized 画点，表现为“拖不动”
        const isManual = String((f as any).source || '').toLowerCase() === 'manual';
        const ow = originalImageDimensions?.width;
        const oh = originalImageDimensions?.height;
        if (isManual && ow && oh) {
          const meta = buildDefaultSquareCoordinateMeta(ow, oh);
          const origNormX = Number(x) || 0;
          const origNormY = Number(y) || 0;
          const origPxX = (origNormX / 1000) * ow;
          const origPxY = (origNormY / 1000) * oh;
          const modelNorm = mapOriginalPixelToModelNormalized(origPxX, origPxY, meta);
          return {
            ...nextBase,
            _analysisCoordMeta: meta,
            _modelNormalizedX: modelNorm.x,
            _modelNormalizedY: modelNorm.y,
          } as Fixture;
        }
        return nextBase;
      });
      
      return {
        ...prev,
        fixtures: updatedFixtures
      };
    });
  }, [saveToUndoHistory, originalImageDimensions]);

  // 🔥 切换光源点锁定状态
  const handleToggleLock = useCallback((id: string) => {
    setProject(prev => {
      // 保存到撤销历史
      saveToUndoHistory(prev.fixtures, prev.selectedFixtureIds);
      
      return {
        ...prev,
        fixtures: prev.fixtures.map(f => {
          if (f.id === id) {
            // 🔥 修复：正确判断当前锁定状态
            const currentLocked = (f as any).locked !== undefined 
              ? (f as any).locked 
              : ((f as any).source === 'auto' || (f as any).source === undefined); // AI识别点默认锁定
            const newLocked = !currentLocked;
            console.log(`切换光源点 ${id} 锁定状态: ${currentLocked} -> ${newLocked}`);
            // 🔥 确保 locked 属性被明确设置（不能是 undefined）
            return { ...f, locked: newLocked };
          }
          return f;
        })
      };
    });
  }, [saveToUndoHistory]);

  // 🔥 删除光源点
  const handleDeleteFixtures = useCallback((ids: string[]) => {
    ids.forEach((id) => {
      delete fixtureBaselineByIdRef.current[id];
    });
    setProject(prev => {
      // 保存到撤销历史
      saveToUndoHistory(prev.fixtures, prev.selectedFixtureIds);
      
      const remainingFixtures = prev.fixtures.filter(f => !ids.includes(f.id));
      const remainingSelectedIds = prev.selectedFixtureIds.filter(id => !ids.includes(id));
      
      // 如果删除后还有光源点，自动选中第一个
      const newSelectedIds = remainingSelectedIds.length > 0 
        ? remainingSelectedIds 
        : (remainingFixtures.length > 0 ? [remainingFixtures[0].id] : []);
      
      console.log(`删除光源点: ${ids.length} 个，剩余: ${remainingFixtures.length} 个`);
      
      return {
        ...prev,
        fixtures: remainingFixtures,
        selectedFixtureIds: newSelectedIds,
        renderedImage: null // 删除光源点后清除渲染结果
      };
    });
  }, [saveToUndoHistory]);

  const updateSelectedFixtures = (updates: Partial<Fixture>) => {
    setProject(prev => {
      // 🔥 只在第一次修改时保存到历史（避免频繁保存）
      const isFirstModify = !prev.fixtures.some(f => 
        prev.selectedFixtureIds.includes(f.id) && f.isModified
      );
      if (isFirstModify) {
        saveToUndoHistory(prev.fixtures, prev.selectedFixtureIds);
      }
      
      return {
        ...prev,
        fixtures: prev.fixtures.map(f => 
          prev.selectedFixtureIds.includes(f.id) 
            ? { ...f, ...updates, isModified: true } 
            : f
        ),
        renderedImage: null
      };
    });
  };

  const checkApiKey = async () => {
    const aistudio = (window as any).aistudio;
    if (aistudio) {
      const hasKey = await aistudio.hasSelectedApiKey();
      if (!hasKey) {
        await aistudio.openSelectKey();
        return true;
      }
      return true;
    }
    return true;
  };

  const handleLogout = async () => {
    // 必须先结束会话并清本地 Supabase 存储，再跳转官网；否则用户点「登录」进入 index-app 时仍会被 getSession 判为已登录
    // P0-A1: 标记主动登出，让 onAuthStateChange 立即绕过 transient grace（0ms 下沉）
    markExplicitAuthClear('user_handleLogout');
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn('[Logout] supabase.auth.signOut 出错:', (e as any)?.message);
    }
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('sb-') || key.includes('supabase')) {
          localStorage.removeItem(key);
        }
      });
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('sb-') || key.includes('supabase')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('[Logout] 清理本地 Supabase 缓存出错:', e);
    }
    try {
      localStorage.removeItem('userEmail');
    } catch {
      /* ignore */
    }
    dispatchRunningHubHistoryReset();
    try {
      sessionStorage.removeItem(NEMO_POST_LOGIN_ADMIN_KEY);
    } catch {
      /* ignore */
    }
    window.location.href = 'index.html';
  };

  /** 与 Sidebar 等旧逻辑对齐：会话有邮箱时写入 localStorage，避免仅依赖 user.email 为空的情况 */
  useEffect(() => {
    const e = resolveAuthEmail(user);
    if (!e) return;
    try {
      localStorage.setItem('userEmail', e);
    } catch {
      /* ignore */
    }
  }, [user]);

  /**
   * Infer lighting params from free-form command text.
   * We keep fixture positions (structure) unchanged; only adjust appearance-related parameters.
   */
  const inferLightingParamsFromCommand = (commandText: string) => {
    const text = (commandText || '').trim();
    const lower = text.toLowerCase();

    let intensity: number | undefined;
    let colorTemp: number | undefined; // -100..100
    let hue: number | undefined; // 0..360 (non-zero triggers hue path)
    let rotation: number | undefined; // 0..360
    let beamAngle: number | undefined; // 15..60

    // Intensity
    if (/(暗|dim|dark|low)/.test(text)) intensity = 0.35;
    else if (/(亮|bright|high|明亮|更亮)/.test(text)) intensity = 0.9;
    else if (/(柔和|soft|gentle)/.test(text)) intensity = 0.6;

    // Color temperature keywords
    if (/(暖|warm|gold|amber)/.test(text)) colorTemp = -50;
    else if (/(冷|cool|blue|daylight)/.test(text)) colorTemp = 50;
    else if (/(中性|neutral|纯白|pure white)/.test(text)) colorTemp = 0;

    // Parse "6500K" style temperature
    const kMatch = text.match(/(\d{3,4})\s*k\b/i);
    if (kMatch) {
      const k = Number(kMatch[1]);
      if (!Number.isNaN(k)) {
        // 6500K is baseline; each 20K step ~= 1 unit in our UI (-100..100)
        const inferred = (k - 6500) / 20;
        colorTemp = Math.max(-100, Math.min(100, inferred));
      }
    }

    // Hue keywords (use hue path in backend by making hue non-zero)
    const hueMap: Array<[RegExp, number]> = [
      [/(红|red)/, 10],
      [/(橙|orange)/, 30],
      [/(黄|yellow)/, 60],
      [/(绿|green)/, 130],
      [/(蓝|blue)/, 230],
      [/(紫|purple)/, 280],
      [/(粉|pink)/, 320],
    ];
    for (const [re, v] of hueMap) {
      if (re.test(lower)) {
        hue = v;
        break;
      }
    }

    // Spotlight direction / beam
    if (/(左|left)/.test(text)) rotation = 180;
    else if (/(右|right)/.test(text)) rotation = 0;
    else if (/(上|up|top)/.test(text)) rotation = 270;
    else if (/(下|down|bottom)/.test(text)) rotation = 90;

    if (/(窄|聚光|tight)/.test(lower)) beamAngle = 20;
    else if (/(宽|漫射|wide)/.test(lower)) beamAngle = 45;

    return { intensity, colorTemp, hue, rotation, beamAngle };
  };

  const handleChatCommandSend = useCallback(
    async (payload: { commandText: string }) => {
      const { commandText } = payload;
      const cmd = (commandText || '').trim();

      if (isAnalyzing || isRendering || project.isScanning || project.isProcessing) {
        alert(language !== 'zh' ? 'Please wait: analyzing/rendering in progress.' : '正在分析/渲染中，请稍候...');
        return;
      }

      if (!cmd) {
        alert(language !== 'zh' ? 'Please enter a command.' : '请输入文字命令');
        return;
      }
      if (!project.renderedImage && !project.originalImage) {
        alert(language !== 'zh' ? 'Please load an image first.' : '请先加载图片');
        return;
      }

      // 仅允许在「已完成灯光生成」的结果图上使用文字命令（避免上传原图后直接一句话绕过灯光工作流）
      const hasDistinctLitResult =
        !!project.renderedImage &&
        (!project.originalImage || project.renderedImage !== project.originalImage);
      if (!hasDistinctLitResult) {
        alert(
          language !== 'zh'
            ? 'Use Generate first to create a lit result. Text commands apply only to that output.'
            : '请先点击「生成」完成灯光渲染。文字命令仅针对已生成结果图，不能仅载入原图后使用。',
        );
        return;
      }

      // 文字命令统一走 commandMode（纯文字编辑当前结果图，单图 gpt-image-2-vip 路径）
      {
        let requestStartedAtCmd = 0;
        const currentWorkingImage = project.renderedImage!;
        // 先点亮进度与处理中状态，再拉取/转 dataURL。否则大图 fetch + readAsDataURL 会卡住主线程数秒，用户以为未发送。
        setIsAnalyzing(false);
        setIsRendering(true);
        setRenderingProgressSource('command');
        setRenderingProgress(3);
        setProject((prev) => ({ ...prev, isScanning: false, isProcessing: true }));

        let safeTarget: string;
        try {
          safeTarget = await ensureImageDataUrl(currentWorkingImage);
        } catch (prepErr) {
          setIsRendering(false);
          setRenderingProgress(0);
          setRenderingProgressSource(null);
          setProject((prev) => ({ ...prev, isProcessing: false }));
          throw prepErr;
        }

        setRenderingProgress(5);

        // 【积分扣费】文字命令渲染前先扣费（与主渲染一致：管理员不在此预扣，由后端按环境扣费/生产免扣）
        const ADMIN_RENDER_EMAIL = '276517619@qq.com';
        if (user?.email && user.email !== ADMIN_RENDER_EMAIL) {
          const { sufficient, required, available } = await checkRenderCredits('render');
          if (!sufficient) {
            alert(`积分不足：灯光渲染需要 ${required} 积分，当前 ${available} 积分，请及时充值`);
            setIsRendering(false);
            setRenderingProgress(0);
            setRenderingProgressSource(null);
            setProject((prev) => ({ ...prev, isProcessing: false }));
            return;
          }
        }
        
        let progressTimerCmd: number | undefined;
        try {
          let dimensions = {
            width: originalImageDimensions?.width || 1024,
            height: originalImageDimensions?.height || 1024,
          };
          if (!originalImageDimensions) {
            await new Promise<void>((resolve) => {
              const img = new Image();
              img.src = safeTarget;
              img.onload = () => {
                dimensions = {
                  width: img.naturalWidth || 1024,
                  height: img.naturalHeight || 1024,
                };
                resolve();
              };
              img.onerror = () => resolve();
            });
          }
          requestStartedAtCmd = Date.now();
          progressTimerCmd = window.setInterval(() => {
            setRenderingProgress((p) => (p >= 92 ? p : Math.min(92, p + 3)));
          }, 700);
          let rendered: Awaited<ReturnType<typeof renderEnhancedLighting>> | null = null;
          try {
            rendered = await renderEnhancedLighting(
              safeTarget,
              [],
              dimensions,
              user?.email,
              cmd,
              true,
              undefined,
              project.depthImageDataUrl && project.normalImageDataUrl
                ? {
                    depthImage: project.depthImageDataUrl,
                    normalImage: project.normalImageDataUrl,
                    coordinateMeta: project.spatialCoordinateMeta ?? undefined,
                  }
                : undefined,
            );
          } catch (err: any) {
            if (isLikelyRenderTimeoutError(err)) {
              const recovered = await tryRecoverRenderedFromRecentHistory(requestStartedAtCmd);
              if (recovered?.renderedImage) {
                const recoveredImage = recovered.renderedImage;
                setRenderingProgress(100);
                setProject((prev) => ({
                  ...prev,
                  originalImage: recovered.originalImage || safeTarget,
                  renderedImage: recoveredImage,
                  fixtures: Array.isArray(recovered.fixtures) ? recovered.fixtures : prev.fixtures,
                  selectedFixtureIds: recovered.activeFixtureId ? [recovered.activeFixtureId] : prev.selectedFixtureIds,
                  isProcessing: false,
                }));
                if (Array.isArray(recovered.fixtures)) replaceFixtureBaseline(recovered.fixtures);
                appendHistoryItemLocal({
                  originalImage: recovered.originalImage || safeTarget,
                  renderedImage: recoveredImage,
                  fixtures: Array.isArray(recovered.fixtures) ? recovered.fixtures : project.fixtures,
                  activeFixtureId: recovered.activeFixtureId || project.selectedFixtureIds[0] || null,
                });
                setShowOriginal(false);
                window.setTimeout(() => {
                  setRenderingProgress(0);
                  setRenderingProgressSource(null);
                }, 2200);
                return;
              }
            }
            setRenderingProgress(0);
            setRenderingProgressSource(null);
            throw err;
          }
          if (!rendered) throw new Error(language !== 'zh' ? 'Render failed' : '渲染失败');
          const renderedImage = displayRenderUrl(rendered);
          setRenderingProgress(100);
          setProject((prev) => ({
            ...prev,
            originalImage: safeTarget,
            renderedImage: renderedImage,
            isProcessing: false,
          }));
          setShowOriginal(false);
          // 文字命令渲染成功后立即写入本地存档（避免后端刷新延迟导致看不到）
          appendHistoryItemLocal({
            originalImage: safeTarget,
            renderedImage: historyRenderUrl(rendered),
            fixtures: project.fixtures,
            activeFixtureId: project.selectedFixtureIds[0] || null,
          });
          // 登录用户再从后端刷新校准（与「生成」入口一致）
          if (isAuthenticated && user?.email) {
            setTimeout(() => {
              fetchHistoryFromDatabase();
            }, 1000);
          }
          void refreshLightingTaskAfterPaidRender();
          window.setTimeout(() => {
            setRenderingProgress(0);
            setRenderingProgressSource(null);
          }, 2200);
        } finally {
          if (progressTimerCmd !== undefined) window.clearInterval(progressTimerCmd);
          setIsAnalyzing(false);
          setIsRendering(false);
          setProject((prev) => ({ ...prev, isScanning: false, isProcessing: false }));
        }
        return;
      }
    },
    // dependencies：须包含文字命令用到的 project 图片字段，否则会闭包陈旧、一直误判「未加载图片」
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAnalyzing,
      isRendering,
      project.isScanning,
      project.isProcessing,
      project.originalImage,
      project.renderedImage,
      language,
      user?.email,
      ensureImageDataUrl,
    ]
  );

  const handleEnhance = async () => {
    // 🔥 渲染并发锁：如果正在渲染，阻止重复调用
    if (isRendering) {
      console.warn("⚠️ 渲染正在进行中，请勿重复操作");
      alert(language !== 'zh' ? "Rendering in progress, please wait..." : "渲染正在进行中，请稍候...");
      return;
    }

    const activeLightingPreset =
      useLightingPresetForRender && selectedLightingPresetId
        ? lightingPresets.find((p) => p.id === selectedLightingPresetId)
        : undefined;
    console.log('[Enhance] generate click', {
      useLightingPresetForRender,
      selectedLightingPresetId,
      presetName: activeLightingPreset?.name,
      colorTempBias: activeLightingPreset?.palette?.colorTempBias,
      saturationDelta: activeLightingPreset?.palette?.saturationDelta,
      intensityDelta: activeLightingPreset?.palette?.intensityDelta,
    });
    
    if (!project.originalImage || project.fixtures.length === 0) {
      alert(language !== 'zh' ? "No image or fixtures detected." : "未检测到图像或灯具。");
      return;
    }

    // 🔥 保险：历史记录可能回填 URL/blob，这里确保进入渲染链路的一定是 dataURL(base64)
    let safeOriginalImage = project.originalImage;
    try {
      safeOriginalImage = await ensureImageDataUrl(project.originalImage);
      if (safeOriginalImage !== project.originalImage) {
        setProject((prev) => ({ ...prev, originalImage: safeOriginalImage }));
      }
    } catch (e: any) {
      console.error('[渲染] ❌ 原图格式转换失败:', e);
      alert(language !== 'zh'
        ? `Render Failed: ${e?.message || 'Invalid image format'}`
        : `渲染失败：${e?.message || '图片格式无效'}`);
      return;
    }

    // 【新积分系统】先扣费，再渲染（管理员不在此预扣：本地由后端扣费；生产环境后端免扣）
    const ADMIN_EMAIL = '276517619@qq.com';
    if (user?.email && user.email !== ADMIN_EMAIL) {
      const { sufficient, required, available } = await checkRenderCredits('render');
      if (!sufficient) {
        alert(`积分不足：灯光渲染需要 ${required} 积分，当前 ${available} 积分，请及时充值`);
        return;
      }
    }

    // 规则：只有被用户调整过的灯光（isModified=true）才允许改变画面。
    // 若没有任何已修改灯光，则保持原图不变，跳过渲染请求（避免“没改也变”）。
    const modifiedFixtures = project.fixtures.filter(f => f.isModified);
    if (modifiedFixtures.length === 0) {
      console.log('[渲染] 🟢 没有已修改灯光：保持原图不变（跳过渲染请求）');
      setProject(prev => ({
        ...prev,
        renderedImage: prev.originalImage || prev.renderedImage,
        isProcessing: false,
      }));
      setShowOriginal(false);
      setRenderingProgress(0);
      setRenderingProgressSource(null);
      setIsRendering(false);
      return;
    }

    await checkApiKey();

    // 🔥 设置渲染并发锁
    setIsRendering(true);
    setProject(prev => ({ ...prev, isProcessing: true }));
    setRenderingProgressSource('generate');
    setRenderingProgress(5);
    const progressTimer = window.setInterval(() => {
      setRenderingProgress((p) => {
        if (p >= 92) return p;
        return Math.min(92, p + 3);
      });
    }, 700);

    // 🔥 关键修复：确保渲染时使用原图尺寸，生成图片尺寸和原图一致
    // 优先使用保存的原图尺寸，如果没有则从 originalImage 获取
    let dimensions = {
      width: 1024,
      height: 1024
    };
    
    // 🔥 优先使用保存的原图尺寸
    if (originalImageDimensions) {
      dimensions = {
        width: originalImageDimensions.width,
        height: originalImageDimensions.height
      };
      console.log(`[渲染] ✅ 使用保存的原图尺寸: ${dimensions.width}x${dimensions.height}（确保输出尺寸和输入一致）`);
    } else {
      // 🔥 如果没有保存的原图尺寸，从原始图片数据获取真实尺寸
      const img = new Image();
      img.src = project.originalImage;
      await new Promise((resolve) => {
        img.onload = () => {
          dimensions = {
            width: img.naturalWidth || 1024,
            height: img.naturalHeight || 1024
          };
          console.log(`[渲染] 🔍 从原图获取尺寸: ${dimensions.width}x${dimensions.height}`);
          // 🔥 保存获取到的原图尺寸
          setOriginalImageDimensions({ width: dimensions.width, height: dimensions.height });
          resolve(null);
        };
        img.onerror = () => {
          console.warn('[渲染] ⚠️ 无法从原图获取尺寸，使用默认值 1024x1024');
          resolve(null);
        };
        // 超时保护
        setTimeout(() => {
          console.warn('[渲染] ⚠️ 获取原图尺寸超时，使用默认值 1024x1024');
          resolve(null);
        }, 2000);
      });
    }
    
    console.log(`[渲染] ✅ 最终使用的渲染尺寸: ${dimensions.width}x${dimensions.height}（确保和原图一致，如500x700输入，输出也是500x700）`);

    console.log('[渲染] 开始渲染，图片尺寸:', dimensions);
    console.log('[渲染] 灯光数量:', project.fixtures.length);
    console.log('[渲染] 已修改的灯光:', project.fixtures.filter(f => f.isModified).length);

    try {
      // 🔥 关键修复：坐标归一化 - 将所有坐标转换为 0-1000 之间的比例值
      // 绝对禁止将还原后的原始像素坐标（如 1148）发送给 /api/gemini/render 接口
      // 公式：x_to_send = (AI识别出的x / 压缩图宽度) * 1000
      //      y_to_send = (AI识别出的y / 压缩图高度) * 1000
      console.log('[渲染] 🔄 开始坐标归一化...');
      console.log('[渲染] 压缩图尺寸:', compressedImageDimensions);

      const ctBias = Number(activeLightingPreset?.palette?.colorTempBias) || 0;
      const satDelta = Number(activeLightingPreset?.palette?.saturationDelta) || 0;
      const intDelta = Number(activeLightingPreset?.palette?.intensityDelta) || 0;
      if (activeLightingPreset) {
        console.log('[渲染] 🎨 已存色板偏置应用到各光源:', { ctBias, satDelta, intDelta });
      }
      
      const normalizedFixtures = project.fixtures.map((f, i) => {
        const source = (f as any).source || 'auto';
        
        let clampedX: number;
        let clampedY: number;
        
        if ((f as any)._analysisCoordMeta && (f as any)._modelNormalizedX !== undefined && (f as any)._modelNormalizedY !== undefined) {
          const restored = mapModelNormalizedToOriginalNormalized(
            (f as any)._modelNormalizedX,
            (f as any)._modelNormalizedY,
            (f as any)._analysisCoordMeta
          );
          clampedX = restored.x;
          clampedY = restored.y;
          console.log(`[坐标归一化] 光源 ${i + 1}: 使用 1024 方图 scale+offset 回推 -> 归一化坐标 (${clampedX}, ${clampedY})`);
        } else if (source === 'manual') {
          // 手动点默认就是 0-1000（原图归一化）；只有明显是像素时（>1000）才按像素归一化
          if (Number(f.x) > 1000 || Number(f.y) > 1000) {
            const normalizedX = Math.round((Number(f.x) / Math.max(1, dimensions.width)) * 1000);
            const normalizedY = Math.round((Number(f.y) / Math.max(1, dimensions.height)) * 1000);
            clampedX = Math.max(0, Math.min(1000, normalizedX));
            clampedY = Math.max(0, Math.min(1000, normalizedY));
            console.log(`[坐标归一化] 手动光源 ${i + 1}: 像素坐标 -> 归一化坐标 (${clampedX}, ${clampedY})`);
          } else {
            clampedX = Math.max(0, Math.min(1000, Number(f.x) || 0));
            clampedY = Math.max(0, Math.min(1000, Number(f.y) || 0));
            console.log(`[坐标归一化] 手动光源 ${i + 1}: 已是 0-1000 坐标 (${clampedX}, ${clampedY})`);
          }
        } else if (Number(f.x) > 1000 || Number(f.y) > 1000) {
          const normalizedX = Math.round((Number(f.x) / Math.max(1, dimensions.width)) * 1000);
          const normalizedY = Math.round((Number(f.y) / Math.max(1, dimensions.height)) * 1000);
          clampedX = Math.max(0, Math.min(1000, normalizedX));
          clampedY = Math.max(0, Math.min(1000, normalizedY));
          console.log(`[坐标归一化] 光源 ${i + 1}: 像素坐标回归一化 -> (${clampedX}, ${clampedY})`);
        } else {
          clampedX = Math.max(0, Math.min(1000, Number(f.x) || 0));
          clampedY = Math.max(0, Math.min(1000, Number(f.y) || 0));
        }
        
        // 🔥 只传递渲染所需字段，保留 source 供后端区分 manual/auto
        const baseSat = f.saturation ?? 100;
        const isCoveType = f.type === 'cove_light' || f.type === 'cove_lighting';
        const sc = isCoveType ? normalizeStripContext((f as any).stripContext) : undefined;
        const sa = isCoveType
          ? normalizeStripAxis((f as any).stripAxis) ?? defaultAxisForStripContext(sc ?? 'generic')
          : undefined;
        const isDlType = f.type === 'downlight' || f.type === 'spotlight';
        const dlc = isDlType ? normalizeDownlightContext((f as any).downlightContext) : undefined;
        const bp = isDlType
          ? normalizeBeamProfile((f as any).beamProfile) ?? defaultBeamProfileForContext(dlc ?? 'generic')
          : undefined;
        const sendFixture = normalizeFixtureForRender({
          id: f.id,
          x: clampedX,
          y: clampedY,
          type: f.type,
          label: f.label,
          source: (f as any).source,
          intensity: Math.max(0, Math.min(2, f.isModified ? (f.intensity + intDelta) : f.intensity)),
          colorTemp: Math.max(-100, Math.min(100, f.isModified ? (f.colorTemp + ctBias) : f.colorTemp)),
          hue: f.hue,
          saturation: Math.max(0, Math.min(100, f.isModified ? (baseSat + satDelta) : baseSat)),
          lightness: f.lightness ?? 50,
          isModified: f.isModified || false,
          rotation: f.rotation,
          beamAngle: f.beamAngle,
          ...(isCoveType && sc !== undefined ? { stripContext: sc, stripAxis: sa } : {}),
          ...(isDlType && dlc !== undefined && bp ? { downlightContext: dlc, beamProfile: bp } : {}),
        });
        return { ...sendFixture } as unknown as Fixture;
      });
      
      // 🔥 调试日志：在发送 render 请求前，打印最终发送的 fixtures 数据
      console.log('[渲染] 🔍 最终发送给后端的 fixtures 数据:');
      normalizedFixtures.forEach((f, i) => {
        console.log(`  [${i + 1}] x: ${f.x}, y: ${f.y}, type: ${f.type}, intensity: ${f.intensity}, isModified: ${f.isModified}`);
        // 🔥 验证：确保 x 和 y 均 ≤ 1000
        if (f.x > 1000 || f.y > 1000 || f.x < 0 || f.y < 0) {
          console.error(`  ❌ 错误：光源 ${i + 1} 的坐标超出范围！x: ${f.x}, y: ${f.y}`);
        }
      });
      
      // 🔥 验证所有坐标都在 0-1000 范围内
      const invalidFixtures = normalizedFixtures.filter(f => f.x < 0 || f.x > 1000 || f.y < 0 || f.y > 1000);
      if (invalidFixtures.length > 0) {
        console.error('[渲染] ❌ 发现坐标超出范围的灯光:', invalidFixtures);
        throw new Error(`坐标验证失败：有 ${invalidFixtures.length} 个灯光的坐标不在 0-1000 范围内`);
      }
      
      console.log('[渲染] ✅ 坐标验证通过：所有坐标都在 0-1000 范围内');
      setRenderingProgress(32);
      
      // 🔥 调试：检查spotlight类型的灯光及其参数
      const spotlightsInData = normalizedFixtures.filter(f => f.type === 'spotlight');
      console.log(`[渲染] 🔍 Spotlight灯光检查: 总数=${spotlightsInData.length}`);
      spotlightsInData.forEach((f, i) => {
        console.log(`[渲染] 🔍 Spotlight ${i + 1}: id=${f.id}, isModified=${f.isModified}, rotation=${(f as any).rotation ?? 'undefined'}, beamAngle=${(f as any).beamAngle ?? 'undefined'}`);
      });
      
      // ⚠️ 重要：先扣费后生图，扣费在后端完成，防止并发导致余额变负
      const rendered = await renderEnhancedLighting(
        safeOriginalImage,
        normalizedFixtures, // 🔥 使用归一化后的坐标（0-1000）
        dimensions,
        user?.email, // 传递用户邮箱，后端会先扣费后生图
        enhanceCommandDraft.trim() || undefined,
        undefined,
        undefined,
        project.depthImageDataUrl && project.normalImageDataUrl
          ? {
              depthImage: project.depthImageDataUrl,
              normalImage: project.normalImageDataUrl,
              coordinateMeta: project.spatialCoordinateMeta ?? undefined,
            }
          : undefined,
      );
      
      if (rendered) {
        console.log('[渲染] ✅ 渲染成功');
        setRenderingProgress(100);
        
        // 🔥 关键：直接使用后端返回的余额更新状态，不等待数据库查询
        const renderedImage = displayRenderUrl(rendered as RenderResultLike);
        setProject(prev => ({ ...prev, renderedImage: renderedImage, isProcessing: false }));
        setIsRendering(false); // 🔥 释放渲染并发锁
        window.clearInterval(progressTimer);
        
        // 🔥 渲染成功后立即写入本地存档，避免后端刷新延迟导致看不到
        appendHistoryItemLocal({
          originalImage: project.originalImage!,
          renderedImage: historyRenderUrl(rendered as RenderResultLike),
          fixtures: project.fixtures,
          activeFixtureId: project.selectedFixtureIds[0] || null,
        });
        // 🔥 登录用户再从数据库重新加载历史记录（确保与数据库同步）
        if (isAuthenticated && user?.email) {
          console.log('[历史记录] 渲染成功，从数据库重新加载历史记录...');
          // 延迟执行，确保后端已保存完成
          setTimeout(() => {
            fetchHistoryFromDatabase();
          }, 1000); // 延迟1秒，确保后端保存完成
        }
        setShowOriginal(false);
        window.setTimeout(() => {
          setRenderingProgress(0);
          setRenderingProgressSource(null);
        }, 2200);

        if (user?.email) {
          console.log('💰 扣费已在后端完成，后台刷新账户信息');
          void refreshAccount({ silent: true });
          setTimeout(() => void refreshAccount({ silent: true }), 1000);
        }

        void refreshLightingTaskAfterPaidRender();
      } else {
        console.error('[渲染] ❌ 渲染返回空结果');
        setProject(prev => ({ ...prev, isProcessing: false }));
        setIsRendering(false); // 🔥 释放渲染并发锁
        window.clearInterval(progressTimer);
        setRenderingProgress(0);
        alert(language !== 'zh' ? 'Render Failed: No result returned' : '渲染失败：未返回结果');
      }
    } catch (err: any) {
      console.error('[渲染] ❌ 渲染错误:', err);
      if (isLikelyRenderTimeoutError(err)) {
        const recovered = await tryRecoverRenderedFromRecentHistory(Date.now() - 30000);
        if (recovered?.renderedImage) {
          const recoveredImage = recovered.renderedImage;
          setRenderingProgress(100);
          setProject((prev) => ({
            ...prev,
            originalImage: recovered.originalImage || prev.originalImage,
            renderedImage: recoveredImage,
            fixtures: Array.isArray(recovered.fixtures) ? recovered.fixtures : prev.fixtures,
            selectedFixtureIds: recovered.activeFixtureId ? [recovered.activeFixtureId] : prev.selectedFixtureIds,
            isProcessing: false,
          }));
          if (Array.isArray(recovered.fixtures)) replaceFixtureBaseline(recovered.fixtures);
          appendHistoryItemLocal({
            originalImage: recovered.originalImage || project.originalImage!,
            renderedImage: recoveredImage,
            fixtures: Array.isArray(recovered.fixtures) ? recovered.fixtures : project.fixtures,
            activeFixtureId: recovered.activeFixtureId || project.selectedFixtureIds[0] || null,
          });
          setIsRendering(false);
          window.clearInterval(progressTimer);
          setShowOriginal(false);
          window.setTimeout(() => {
            setRenderingProgress(0);
            setRenderingProgressSource(null);
          }, 2200);
          return;
        }
      }
      setProject(prev => ({ ...prev, isProcessing: false }));
      setIsRendering(false); // 🔥 确保无论成功或失败都释放渲染并发锁
      window.clearInterval(progressTimer);
      setRenderingProgress(0);
      setRenderingProgressSource(null);
      
      // ⚠️ 重要：API 调用失败时，后端已自动回退扣费，前端不执行任何数据库更新操作
      console.log('⚠️ [前端] API 调用失败，后端已自动回退扣费，前端不执行数据库更新');
      
      const errorMsg = err.message || "";
      const isInsufficientBalance = err.isInsufficientBalance || err.status === 402 || err.status === 403;
      
      // 🔥 精确匹配错误类型：区分余额不足和并发冲突
      if (isInsufficientBalance || errorMsg === "余额不足" || errorMsg.includes("余额不足") || errorMsg.includes("无法生成图片") || errorMsg.includes('积分不足或已过期')) {
        alert(
          language !== 'zh'
            ? 'Insufficient credits or expired. Please recharge.'
            : '积分不足或已过期，请及时充值'
        );
        setShowPaymentCenter(true);
        return;
      } else if (errorMsg.includes("已被其他请求修改") || errorMsg.includes("并发冲突")) {
        // 并发冲突错误，提示重试而不是充值
        alert(
          language !== 'zh' 
            ? 'Network busy, please try again later.'
            : '网络繁忙，请稍后重试。'
        );
        return;
      }
      
      // 检查是否是网络错误
      if (errorMsg.includes("fetch") || errorMsg.includes("network") || errorMsg.includes("Failed to fetch")) {
        alert(language !== 'zh' 
          ? "Network error: Cannot connect to backend. Please check if the backend service is running."
          : "网络错误：无法连接到后端。请检查后端服务是否正在运行。");
        return;
      }
      
      if (errorMsg.includes("PERMISSION_DENIED") || errorMsg.includes("403") || errorMsg.includes("Requested entity was not found")) {
        const confirmMsg = language !== 'zh' 
          ? "Permission denied. Gemini 3 Pro requires a paid API key with billing enabled. Would you like to select a new key?\n\nSee billing docs: https://ai.google.dev/gemini-api/docs/billing"
          : "权限被拒绝。Gemini 3 Pro 需要启用结算功能的付费 API 密钥。是否要选择新密钥？\n\n查看结算文档：https://ai.google.dev/gemini-api/docs/billing";
        
        if (window.confirm(confirmMsg)) {
          const aistudio = (window as any).aistudio;
          if (aistudio) await aistudio.openSelectKey();
        }
      } else {
        alert(`${language !== 'zh' ? 'Render Failed' : '渲染失败'}: ${err.message || 'Unknown Error'}`);
      }
    }
  };

  const healthReachabilityBanner =
    healthBackendUnreachable && !isMaintenanceMode ? (
      <div
        className="fixed top-0 left-0 right-0 z-[1999] pointer-events-none flex justify-center px-3 pt-2"
        role="status"
        aria-live="polite"
      >
        <div
          className={`pointer-events-auto text-xs sm:text-sm px-3 py-1.5 rounded-md shadow-md ${
            isDarkMode
              ? 'bg-amber-950/90 text-amber-100 border border-amber-800/50'
              : 'bg-amber-50 text-amber-900 border border-amber-200'
          }`}
        >
          {language === 'zh'
            ? '后端连接不稳定，正在重试'
            : 'Backend connection unstable, retrying…'}
        </div>
      </div>
    ) : null;

  // 如果还在检查认证状态，显示加载中（避免网关/实验室来回闪烁）
  if (isAuthenticated === null) {
    return (
      <div className="flex w-screen h-screen items-center justify-center bg-black">
        <div className="text-white text-xl">{translateAppI18n('app_auth_loading', language)}</div>
      </div>
    );
  }

  // 从落地页 注册/登录 链接进入时（#signup / #login）：未登录则全屏认证弹窗；已登录且 #signup 时在实验室内叠加注册弹窗
  const authHash = (() => {
    const h = window.location.hash.slice(1).toLowerCase();
    return h === 'signup' || h === 'login' ? h : null;
  })();

  // 未认证：仅显示登录/注册弹窗，不再显示 React 版的 NEMOLIGHTLAB 网关页面
  if (!isAuthenticated) {
    return (
      <>
      {healthReachabilityBanner}
      <AuthModal
        language={language}
        isDarkMode={isDarkMode}
        initialMode={authHash === 'signup' ? 'signup' : 'login'}
        onClose={() => {
          try {
            sessionStorage.removeItem(PENDING_PAYMENT_FROM_LANDING_KEY);
          } catch {}
          // 通过官网入口（#signup / #login）打开时，关闭弹窗直接回到静态官网
          if (authHash) {
            window.location.href = 'index.html';
          }
        }}
        onAuthSuccess={async () => {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            const isEmailConfirmed = (session.user.email_confirmed_at != null) ||
                                   ((session.user as any).confirmed_at != null) ||
                                   ((session.user as any).email_verified === true);
            if (!isEmailConfirmed) {
              // P0-A1: AuthModal 内邮箱未确认强制 signOut 绕过 transient grace
              markExplicitAuthClear('email_not_confirmed_modal_main');
              await supabase.auth.signOut();
              localStorage.clear();
              sessionStorage.clear();
              setIsAuthenticated(false);
              return;
            }
          }
            setIsAuthenticated(true);
            void ensurePublicUserOnLogin().then(() =>
          refreshAccount({ silent: true, reason: 'auth_session_ready' }),
        );
            if (authHash) {
              window.history.replaceState({}, '', window.location.pathname);
              setRouteHashNorm('');
            } else {
              setRouteHashNorm(readHashNorm());
            }
          }}
          onLanguageChange={(newLang) => setLanguage(newLang)}
      />
      </>
    );
  }

  let adminRoutePending = false;
  try {
    adminRoutePending =
      isAuthenticated === true && sessionStorage.getItem(NEMO_POST_LOGIN_ADMIN_KEY) === '1';
  } catch {
    adminRoutePending = false;
  }
  const isAdminHash =
    isAuthenticated === true && (routeHashNorm === 'admin' || adminRoutePending);

  if (isAdminHash) {
    return (
      <>
      {healthReachabilityBanner}
      <Suspense
        fallback={
          <div className="flex w-screen h-screen items-center justify-center bg-gray-100 text-gray-700">
            加载管理后台…
          </div>
        }
      >
        <AdminLazyPage />
      </Suspense>
      </>
    );
  }

  return (
    <ToastProvider>
      {healthReachabilityBanner}
      {showPasswordResetOverlay ? (
        <PasswordResetScreen
          language={language}
          isDarkMode={isDarkMode}
          onDone={() => setShowPasswordResetOverlay(false)}
        />
      ) : (
      <>
      {authHash === 'signup' && (
        <AuthModal
          language={language}
          isDarkMode={isDarkMode}
          initialMode="signup"
          onClose={() => {
            try {
              sessionStorage.removeItem(PENDING_PAYMENT_FROM_LANDING_KEY);
            } catch {}
            window.history.replaceState({}, '', window.location.pathname);
            setRouteHashNorm('');
          }}
          onAuthSuccess={async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
              const isEmailConfirmed = (session.user.email_confirmed_at != null) ||
                                     ((session.user as any).confirmed_at != null) ||
                                     ((session.user as any).email_verified === true);
              if (!isEmailConfirmed) {
                // P0-A1: 备份 AuthModal 邮箱未确认强制 signOut 绕过 transient grace
                markExplicitAuthClear('email_not_confirmed_modal_fallback');
                await supabase.auth.signOut();
                localStorage.clear();
                sessionStorage.clear();
                setIsAuthenticated(false);
                setUser(null);
                return;
              }
            }
            setIsAuthenticated(true);
            setUser(session?.user ?? null);
            void ensurePublicUserOnLogin().then(() =>
          refreshAccount({ silent: true, reason: 'auth_session_ready' }),
        );
            window.history.replaceState({}, '', window.location.pathname);
            setRouteHashNorm('');
          }}
          onLanguageChange={(newLang) => setLanguage(newLang)}
        />
      )}
      <div className={`flex w-screen h-screen transition-colors duration-500 overflow-hidden ${isDarkMode ? 'bg-black' : 'bg-zinc-50'}`}>
        <AnimatePresence mode="wait">
          <motion.div 
            key="main"
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="nemo-skin flex flex-col md:flex-row w-full h-full relative"
          >
            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden fixed top-4 left-4 z-[2000] w-12 h-12 bg-[#D7CDA1]/90 hover:bg-[#D7CDA1] text-white rounded-lg flex items-center justify-center shadow-lg transition-all"
              aria-label={language !== 'zh' ? 'Toggle menu' : '切换菜单'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {sidebarOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* 移动端遮罩层 */}
            {sidebarOpen && (
              <div
                className="md:hidden fixed inset-0 bg-black/50 z-[1998]"
                onClick={() => setSidebarOpen(false)}
              />
            )}

            <Editor 
              selectedFixtureIds={project.selectedFixtureIds}
              fixtures={project.fixtures}
              originalImage={project.originalImage}
              renderedImage={project.renderedImage} // 🔥 修复：实时预览需要 renderedImage，不应该因为 showOriginal 而丢失
              updateSelectedFixtures={updateSelectedFixtures}
              onSelectFixture={handleSelectFixture}
              onAddFixture={handleAddFixture}
              onMoveFixture={handleMoveFixture}
              onToggleLock={handleToggleLock}
              onDeleteFixtures={handleDeleteFixtures}
              onUndo={handleUndo}
              isProcessing={project.isProcessing || isRendering}
              isScanning={project.isScanning || isAnalyzing}
              showOriginal={showOriginal}
              onSwitchToOriginal={() => setShowOriginal(true)}
              onSwitchToRendered={() => setShowOriginal(false)}
              isDarkMode={isDarkMode}
              language={language}
              isEditMode={isEditMode}
              selectedLightSourceType={selectedLightSourceType}
              selectedLightSourceLabel={selectedLightSourceLabel}
              manualStripContext={manualStripContext}
              manualDownlightContext={manualDownlightContext}
              onCommandSend={handleChatCommandSend}
              onCommandDraftChange={setEnhanceCommandDraft}
              renderingProgress={renderingProgress}
              renderingProgressSource={renderingProgressSource}
              recognitionScanPhase={recognitionScanPhase}
              onUpload={handleUpload}
            />
            {/* 支付提示消息 */}
            <AnimatePresence>
              {paymentMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -50 }}
                  className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[600] bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{paymentMessage}</span>
                  <button
                    onClick={() => setPaymentMessage(null)}
                    className="ml-2 text-white hover:text-gray-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* 充值中心组件 */}
            {showPaymentCenter && user?.email && (
              <PaymentCenter
                userEmail={user.email}
                language={language}
                isDarkMode={isDarkMode}
                autoRechargePlanId={autoRechargePlanId ?? undefined}
                returnUrlOverride={returnUrlOverride}
                onClose={() => {
                  setShowPaymentCenter(false);
                  setAutoRechargePlanId(null);
                  setReturnUrlOverride(null);
                  // 关闭充值中心后刷新账户信息
                  if (user.email) {
                    void refreshAccount({ silent: true, reason: 'payment_center_close' });
                  }
                }}
              />
            )}

            {/* 用户账户组件 */}
            {showUserAccount && user?.email && (
              <UserAccount
                userEmail={user.email}
                language={language}
                isDarkMode={isDarkMode}
                onClose={() => setShowUserAccount(false)}
                onOpenPaymentCenter={() => {
                  setShowUserAccount(false);
                  setAutoRechargePlanId(null);
                  setReturnUrlOverride(null);
                  setShowPaymentCenter(true);
                }}
              />
            )}

            <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:relative inset-y-0 left-0 z-[1999] md:z-auto transition-transform duration-300 ease-in-out flex flex-col min-h-0 ${isDarkMode ? 'bg-black' : 'bg-zinc-50'}`}>
              <Sidebar 
                selectedFixtureIds={project.selectedFixtureIds}
                fixtures={project.fixtures}
                onSelectFixture={handleSelectFixture}
                updateSelectedFixtures={updateSelectedFixtures}
                onUpload={handleUpload}
                isScanning={project.isScanning || isAnalyzing} // 🔥 传递分析状态（双重保险：project.isScanning 或 isAnalyzing）
                onRecognize={handleRecognizeFixtures}
                recognitionIdentifyExhausted={lightingIdentifyCount >= 2}
                recognitionAlreadyCompleted={recognitionCompletedForCurrentImage}
                triPipelineSpatialReady={Boolean(
                  project.depthImageDataUrl && project.normalImageDataUrl,
                )}
                onToggleLock={handleToggleLock}
                onEnhance={handleEnhance}
                onReset={handleResetFixturesToBaseline}
              onDownload={async () => {
                 if (!project.renderedImage) return;
                 try {
                   // 导入水印函数
                   const { addWatermarkToImage, generatedImageDownloadFilename } = await import('./components/Watermark');
                   // 添加水印到图片
                   const watermarkedImage = await addWatermarkToImage(project.renderedImage);
                   // 创建下载链接
                   const link = document.createElement('a');
                   link.href = watermarkedImage;
                   link.download = generatedImageDownloadFilename(Date.now());
                   link.click();
                } catch (error) {
                  console.error('下载失败:', error);
                  alert(
                    language === 'en'
                      ? 'Download failed. Sign in and retry if the image is from cloud storage.'
                      : '下载失败，请登录账号后重试（云端图片需后端拉取才能加水印）。',
                  );
                }
              }}
              // 🔒🔒🔒 永久锁死代码 - 打开历史存档功能 🔒🔒🔒
              // ⚠️⚠️⚠️ 警告：此代码已被"永久锁死"，绝对禁止随意修改！
              // 功能：任何人都可以在不载入图片的情况下查看历史存档
              // 要求：必须始终可用，不受任何条件限制
              // 锁死日期：2026-02-11
              // 🔒🔒🔒 未经用户明确要求，绝对禁止修改以下代码 🔒🔒🔒
              onOpenHistory={() => {
                console.log('[App.tsx] 🔥🔥🔥 onOpenHistory 被调用 🔥🔥🔥');
                console.log('[App.tsx] 当前 history 长度:', history.length);
                console.log('[App.tsx] 当前 isHistoryOpen 状态:', isHistoryOpen);
                
                // 🔥🔥🔥 最简单直接的方式：立即打开面板并立即加载历史记录 🔥🔥🔥
                console.log('[App.tsx] 正在设置 isHistoryOpen = true...');
                setIsHistoryOpen(true);
                console.log('[App.tsx] ✅ setIsHistoryOpen(true) 已调用');
                
                // 🔥 立即加载历史记录（不等待，不延迟）
                console.log('[App.tsx] 🔥 立即加载历史记录（不延迟）...');
                fetchHistoryFromDatabase().catch(err => {
                  console.error('[App.tsx] ❌ 加载历史记录失败:', err);
                });
              }}
              onUpscale={() => setIsUpscaleOpen(true)}
              onAngleTransform={() => setIsLightReconstructOpen(true)}
              onTextReconstruct={() => setIsTextReconstructOpen(true)}
              onPhysicalReconstruct={() => setIsPhysicalReconstructOpen(true)}
              onToneMapping={() => setIsToneMappingOpen(true)}
              onDepth={() => setIsDepthModalOpen(true)}
              onOpenPhotopea={handleOpenPhotopea}
              isAuthenticated={isAuthenticated === true}
              lightingPresets={lightingPresets}
              useLightingPresetForRender={useLightingPresetForRender}
              onUseLightingPresetForRenderChange={setUseLightingPresetForRender}
              selectedLightingPresetId={selectedLightingPresetId}
              onSelectedLightingPresetIdChange={setSelectedLightingPresetId}
              historyCount={history.length}
              isProcessing={project.isProcessing || isRendering} // 🔥 传递渲染状态（双重保险：project.isProcessing 或 isRendering）
              renderingProgress={renderingProgress}
              renderingProgressSource={renderingProgressSource}
              hasImage={!!project.originalImage}
              hasRendered={!!project.renderedImage}
              fixturesFound={project.fixtures.length}
              isDarkMode={isDarkMode}
              onToggleTheme={() => setIsDarkMode(!isDarkMode)}
              language={language}
              onSetLanguage={(l) => setLanguage(l)}
              onLogout={handleLogout}
              onOpenPaymentCenter={() => {
                setAutoRechargePlanId(null);
                setReturnUrlOverride(null);
                setShowPaymentCenter(true);
              }}
              onOpenUserAccount={() => {
                setShowUserAccount(true);
                setSidebarOpen(false);
              }}
              onAddLightSource={(lightType: string, label: string) => {
                // 🔥 进入编辑模式，设置选中的光源类型和标签
                setIsEditMode(true);
                setSelectedLightSourceType(lightType);
                setSelectedLightSourceLabel(label);
                console.log('进入编辑模式，选择光源类型:', lightType, '标签:', label);
              }}
              onConfirmLightSources={() => {
                // 🔥 确认添加的光源，退出编辑模式
                setIsEditMode(false);
                setSelectedLightSourceType(null);
                setSelectedLightSourceLabel(null);
                console.log('确认添加光源，退出编辑模式');
              }}
              isEditMode={isEditMode}
              selectedLightSourceType={selectedLightSourceType}
              manualStripContext={manualStripContext}
              onManualStripContextChange={setManualStripContext}
              manualDownlightContext={manualDownlightContext}
              onManualDownlightContextChange={setManualDownlightContext}
              onBackToSite={() => {
                // 返回站点时，统一回到静态官网首页，而不是 React 网关页
                window.location.href = 'index.html';
              }}
              authenticatedUserEmail={resolveAuthEmail(user) || null}
            />
            </div>
            <HistoryPanel 
              isOpen={isHistoryOpen}
              onClose={() => {
                console.log('[App.tsx] HistoryPanel onClose 被调用，设置 isHistoryOpen = false');
                setIsHistoryOpen(false);
              }}
              items={history}
              isDarkMode={isDarkMode}
              language={language}
              onSelectItem={async (item) => {
                console.log('[App.tsx] 🔥 选择历史记录项:', item.id);
                console.log('[App.tsx] 🔥 设置 showOriginal = false');
                setShowOriginal(false); // 🔥 确保显示渲染结果，不是原始图片
                setIsHistoryOpen(false);

                // 先立即恢复会话（渲染图 + 原图 URL），浏览器马上开始拉 COS；避免整段卡在「下载原图→转 base64」才更新界面
                setProject({
                  originalImage: item.originalImage,
                  renderedImage: item.renderedImage,
                  fixtures: item.fixtures,
                  selectedFixtureIds: item.activeFixtureId ? [item.activeFixtureId] : [],
                  isProcessing: false,
                  isScanning: false,
                  depthImageDataUrl: null,
                  normalImageDataUrl: null,
                  spatialCoordinateMeta: null,
                });
                replaceFixtureBaseline(item.fixtures);

                setLightingIdentifyCount(0);
                lightingIdentifyCountRef.current = 0;

                if (user?.email) {
                  await createLightingTaskForCurrentUser();
                } else {
                  lightingTaskIdRef.current = null;
                  setLightingTaskId(null);
                }

                try {
                  const fixedOriginal = await ensureImageDataUrl(item.originalImage);
                  setProject((prev) => ({ ...prev, originalImage: fixedOriginal }));
                } catch (e: any) {
                  console.error('[历史记录] ❌ 原图转 data URL 失败:', e);
                  alert(language !== 'zh'
                    ? `Failed to load original image: ${e?.message || 'Unknown error'}`
                    : `历史记录原图加载失败：${e?.message || '未知错误'}`);
                }
              }}
              onDeleteItem={async (id) => {
                // 🔥 如果用户已登录，从数据库删除；否则只从本地删除
                if (isAuthenticated && user?.email) {
                  setHistory((prev) => prev.filter((i) => i.id !== id));
                  try {
                    await api.deleteHistory(id);
                    console.log('[历史记录] ✅ 已从数据库删除:', id);
                    await fetchHistoryFromDatabase([id]);
                  } catch (error: any) {
                    console.error('[历史记录] ❌ 删除失败:', error);
                    alert(language !== 'zh' ? 'Failed to delete history item' : '删除历史记录失败');
                    await fetchHistoryFromDatabase();
                  }
                } else {
                  setHistory((prev) => prev.filter((i) => i.id !== id));
                }
              }}
            />
            <UpscalePanel
              isOpen={isUpscaleOpen}
              onClose={() => setIsUpscaleOpen(false)}
              sourceImage={project.renderedImage || project.originalImage}
              isDarkMode={isDarkMode}
              language={language}
              userEmail={user?.email}
              isAuthenticated={isAuthenticated === true}
              recoveryRequest={rhRecoveryRequest?.featureType === 'upscale' ? rhRecoveryRequest : null}
            />
            <LightReconstructPanel
              isOpen={isLightReconstructOpen}
              onClose={() => setIsLightReconstructOpen(false)}
              sourceImage={project.renderedImage || project.originalImage}
              isDarkMode={isDarkMode}
              language={language}
              userEmail={user?.email}
              isAuthenticated={isAuthenticated === true}
              recoveryRequest={rhRecoveryRequest?.featureType === 'light_reconstruct' ? rhRecoveryRequest : null}
            />
            <TextReconstructPanel
              isOpen={isTextReconstructOpen}
              onClose={() => setIsTextReconstructOpen(false)}
              sourceImage={project.renderedImage || project.originalImage}
              isDarkMode={isDarkMode}
              language={language}
              userEmail={user?.email}
              isAuthenticated={isAuthenticated === true}
              recoveryRequest={rhRecoveryRequest?.featureType === 'text_reconstruct' ? rhRecoveryRequest : null}
            />
            <PhysicalReconstructPanel
              isOpen={isPhysicalReconstructOpen}
              onClose={() => setIsPhysicalReconstructOpen(false)}
              sourceImage={project.renderedImage || project.originalImage}
              isDarkMode={isDarkMode}
              language={language}
              userEmail={user?.email}
              isAuthenticated={isAuthenticated === true}
              recoveryRequest={rhRecoveryRequest?.featureType === 'physical_reconstruct' ? rhRecoveryRequest : null}
            />
            <ToneMappingModal
              isOpen={isToneMappingOpen}
              onClose={() => setIsToneMappingOpen(false)}
              isDarkMode={isDarkMode}
              language={language}
              userEmail={user?.email ?? null}
              isAuthenticated={isAuthenticated === true}
              recoveryRequest={rhRecoveryRequest?.featureType === 'tone_mapping' ? rhRecoveryRequest : null}
            />
            <DepthModal
              isOpen={isDepthModalOpen}
              onClose={() => setIsDepthModalOpen(false)}
              language={language}
              userEmail={user?.email ?? null}
              projectImage={project.originalImage ?? null}
            />
            <RecognitionSyncReportModal
              isOpen={showRecognitionSyncReport}
              onClose={() => setShowRecognitionSyncReport(false)}
              report={recognitionSyncReport}
              language={language}
            />
            <RunningHubJobRecoveryBar
              language={language}
              isAuthenticated={isAuthenticated === true}
              userEmail={user?.email}
              onRecoverCompleted={(req) => setRhRecoveryRequest(req)}
              panelOpeners={{
                setIsUpscaleOpen,
                setIsToneMappingOpen,
                setIsLightReconstructOpen,
                setIsTextReconstructOpen,
                setIsPhysicalReconstructOpen,
              }}
            />
          </motion.div>
      </AnimatePresence>
      </div>
      </>
      )}
    </ToastProvider>
  );
};

export default App;
